"use strict";

const HOME_JS_BUILD = '2026-07-14-title-anchored-handoff-v5';
const LOTTIE_SCENE_FAILSAFE_MS = 15000;
const CRITICAL_LOTTIE_MAX_MS = 45000;
const PAGE_LOADER_MAX_MS = 45000;
/* ============================================================================
 * EXPLODED HEADPHONE ANIMATION — frame map
 * The exploded Lottie (explodedAnimation.json) has 114 frames:
 *   frame 0   = headphone fully assembled
 *   frame 82  = "minor explosion" (ear pad and driver assembly separated —
 *               this is the resting pose where the driver labels are shown)
 *   frame 114 = fully exploded (all internal parts spread apart)
 * ==========================================================================*/
const EXPLODED_FIRST_FRAME = 0;
const EXPLODED_MIDDLE_FRAME = 82;
const EXPLODED_LAST_FRAME = 114;
/* Scroll-scrub rendering resolution for the exploded Lottie.
 * The canvas is re-rendered only on every Nth frame while scrolling
 * (114 / 6 ≈ 19 visible steps) instead of all 114 frames. This keeps the
 * explode looking like a gradual, progressive explosion (previously it
 * snapped between just 3 poses and looked like an instant jump) while still
 * skipping ~5 out of every 6 canvas renders for scroll performance.
 * Lower value = smoother explode, higher value = better performance. */
const EXPLODED_FRAME_SNAP_STEP = 6;
const LOTTIE_GATED_SCROLL_TRIGGER_IDS = new Set([
    'explodedSection',
    'fullyImmersiveSection',
    'hyperPersonalizedIntroTimeline',
    'hearTimeline',
    'feelTimeline',
    'immerseTimeline',
    'beastTimeline',
]);
const SECTION_LAZY_ROOT_MARGIN = '400px 0px';
const HERO_SECTION_IDS = new Set(['heroSection', 'heroHeadphone']);
console.info('[home1.js] build:', HOME_JS_BUILD);

function _getLazySections() {
    return [...document.querySelectorAll('#homePage section')].filter(
        (section) => !HERO_SECTION_IDS.has(section.id),
    );
}

function _deferLazyImage(img) {
    if (!img || img.closest('#heroHeadphone, #heroSection')) {
        return;
    }

    if (img.dataset.lazySrc) {
        return;
    }

    const src = img.getAttribute('src');
    if (!src || src.startsWith('data:')) {
        return;
    }

    img.dataset.lazySrc = src;
    img.removeAttribute('src');
}

function _deferLazySvgImage(imageEl) {
    if (!imageEl || imageEl.dataset.lazyHref) {
        return;
    }

    const href = imageEl.getAttribute('href')
        || imageEl.getAttributeNS('http://www.w3.org/1999/xlink', 'href');

    if (!href || href.startsWith('data:')) {
        return;
    }

    imageEl.dataset.lazyHref = href;
    imageEl.removeAttribute('href');
    imageEl.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
}

function _loadLazyImagesIn(root) {
    if (!root || root.dataset.lazyImagesLoaded === 'true') {
        return;
    }

    root.dataset.lazyImagesLoaded = 'true';

    root.querySelectorAll('img[data-lazy-src]').forEach((img) => {
        img.src = img.dataset.lazySrc;
        delete img.dataset.lazySrc;
    });

    root.querySelectorAll('image[data-lazy-href]').forEach((imageEl) => {
        const href = imageEl.dataset.lazyHref;
        imageEl.setAttributeNS('http://www.w3.org/1999/xlink', 'href', href);
        imageEl.setAttribute('href', href);
        delete imageEl.dataset.lazyHref;
    });
}

function _deferHomePageImagesEarly() {
    _getLazySections().forEach((section) => {
        section.querySelectorAll('img[src]').forEach(_deferLazyImage);
        section.querySelectorAll('image').forEach(_deferLazySvgImage);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _deferHomePageImagesEarly, { once: true });
} else {
    _deferHomePageImagesEarly();
}

// CRITICAL: Run timeout interception IMMEDIATELY to prevent 20+ second blocking
// This must run before any other code to catch slow requests early
(function() {
    const REQUEST_TIMEOUT = 2000; // 2 second timeout instead of 21 seconds
    
    // Intercept fetch requests IMMEDIATELY
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
        const url = args[0];
        if (typeof url === 'string' && (url.includes('hop?') || url.includes('analytics_trace_id'))) {
            // Add timeout to prevent 21-second blocking
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Request timeout')), REQUEST_TIMEOUT);
            });
            
            return Promise.race([
                originalFetch.apply(this, args),
                timeoutPromise
            ]).catch(error => {
                // Silently fail - don't block page load
                return new Response(null, { status: 408, statusText: 'Request Timeout' });
            });
        }
        return originalFetch.apply(this, args);
    };

    // Intercept XMLHttpRequest IMMEDIATELY for hop requests
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;
    
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
        this._url = url;
        this._isHopRequest = typeof url === 'string' && (url.includes('hop?') || url.includes('analytics_trace_id'));
        return originalXHROpen.apply(this, [method, url, ...rest]);
    };

    XMLHttpRequest.prototype.send = function(...args) {
        if (this._isHopRequest) {
            // Add timeout
            const timeoutId = setTimeout(() => {
                if (this.readyState !== XMLHttpRequest.DONE) {
                    this.abort();
                }
            }, REQUEST_TIMEOUT);
            
            this.addEventListener('loadend', () => {
                clearTimeout(timeoutId);
            });
        }
        return originalXHRSend.apply(this, args);
    };
})();

// Register GSAP plugins
if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const ViewportSize = {
    small: 'small',
    medium: 'medium',
    large: 'large',
}

class HomePage {
    constructor() {
        this._scrollTimelines = [];

        this._viewportSize = this._identifyViewportSize();

        this._otherFeaturesCarousel = undefined;
        this._otherFeaturesImageCarousel = undefined;

        this._isZohoFormShown = false;
        
        this._otherFeaturesAutoplayResumeTimeout = null;
        this._otherFeaturesScrollSlideIndex = -1;
        this._trustBarCountersPlayed = false;
        this._windowLoadHandled = false;
        this._readyLottieNames = new Set();
    }

    _formatTrustBarCount(value, useCommas) {
        const rounded = Math.round(value);
        return useCommas ? rounded.toLocaleString('en-US') : String(rounded);
    }

    _resetTrustBarCounters() {
        document.querySelectorAll('.trustBarValueCount').forEach((element) => {
            const numberEl = element.querySelector('.trustBarCountNumber');
            if (element._countTween) {
                element._countTween.kill();
                element._countTween = null;
            }
            if (numberEl) {
                numberEl.textContent = '1';
            }
        });
    }

    _playTrustBarCounters() {
        if (typeof gsap === 'undefined') return;

        document.querySelectorAll('.trustBarValueCount').forEach((element) => {
            const target = parseInt(element.dataset.countTo, 10);
            const suffix = element.dataset.countSuffix || '';
            const numberEl = element.querySelector('.trustBarCountNumber');
            if (!numberEl || Number.isNaN(target)) return;

            if (element._countTween) {
                element._countTween.kill();
            }

            const useCommas = target >= 1000;
            const counter = { value: 1 };

            element._countTween = gsap.to(counter, {
                value: target,
                duration: target >= 1000 ? 2.2 : 1.8,
                ease: 'power2.out',
                onUpdate: () => {
                    numberEl.textContent = this._formatTrustBarCount(counter.value, useCommas) + suffix;
                },
                onComplete: () => {
                    numberEl.textContent = this._formatTrustBarCount(target, useCommas) + suffix;
                    element._countTween = null;
                },
            });
        });
    }

    _resetTrustBarCounterState() {
        this._trustBarCountersPlayed = false;
        this._resetTrustBarCounters();
    }

    _initializeTrustBarCounters() {
        this._resetTrustBarCounterState();
    }

    _setOtherFeaturesSlideFromScroll(progress) {
        if (!this._otherFeaturesCarousel) return;

        const totalSlides = document.querySelectorAll('#otherFeaturesCarousel .swiper-slide[data-slide-label]').length || 4;
        const targetSlideIndex = progress >= 1
            ? totalSlides - 1
            : Math.min(Math.floor(progress * totalSlides), totalSlides - 1);

        if (targetSlideIndex === this._otherFeaturesScrollSlideIndex) return;

        this._otherFeaturesScrollSlideIndex = targetSlideIndex;
        if (this._otherFeaturesCarousel.autoplay) {
            this._otherFeaturesCarousel.autoplay.stop();
        }
        this._otherFeaturesCarousel.slideToLoop(targetSlideIndex);
    }

    _isMobileViewport() {
        return window.innerWidth < 750;
    }

    _getCriticalImageElements() {
        const elements = new Set();
        const selectors = [
            '#heroHeadphone img',
            '#earprintPhone',
            '#otherFeaturesSection img',
            '#otherFeaturesImageCarousel img',
            '#otherFeaturesImageCarousel image',
        ];

        selectors.forEach((selector) => {
            document.querySelectorAll(selector).forEach((el) => elements.add(el));
        });
        document.querySelectorAll('#homePage image').forEach((el) => elements.add(el));

        return [...elements];
    }

    _waitForCriticalImages() {
        const elements = this._getCriticalImageElements();
        const tasks = [];
        const svgUrls = new Set();

        elements.forEach((el) => {
            if (el.tagName === 'IMG') {
                tasks.push(this._waitForHtmlImage(el));
                return;
            }

            if (el.tagName !== 'image') {
                return;
            }

            const href = el.getAttribute('href')
                || el.getAttributeNS('http://www.w3.org/1999/xlink', 'href');

            if (!href || href.startsWith('data:') || svgUrls.has(href)) {
                return;
            }

            svgUrls.add(href);
            tasks.push(this._waitForSvgImage(el));
        });

        if (!tasks.length) {
            return Promise.resolve();
        }

        return Promise.all(tasks);
    }

    _waitForHtmlImage(img) {
        if (img.complete) {
            if (img.naturalWidth > 0 && typeof img.decode === 'function') {
                return img.decode().catch(() => undefined);
            }
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            const done = () => resolve();
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
        });
    }

    _waitForSvgImage(imageEl) {
        const href = imageEl.getAttribute('href')
            || imageEl.getAttributeNS('http://www.w3.org/1999/xlink', 'href');

        if (!href || href.startsWith('data:')) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            const image = new Image();
            image.decoding = 'async';
            image.onload = resolve;
            image.onerror = resolve;
            image.src = href;
        });
    }

    _waitForAllPageImages() {
        const htmlImages = [...document.querySelectorAll('#homePage img[src]')];
        const svgImages = [...document.querySelectorAll('#homePage image')];
        const svgUrls = new Set();
        const svgTasks = [];

        svgImages.forEach((imageEl) => {
            const href = imageEl.getAttribute('href')
                || imageEl.getAttributeNS('http://www.w3.org/1999/xlink', 'href');

            if (!href || href.startsWith('data:') || svgUrls.has(href)) {
                return;
            }

            svgUrls.add(href);
            svgTasks.push(this._waitForSvgImage(imageEl));
        });

        return Promise.all([
            ...htmlImages.map((img) => this._waitForHtmlImage(img)),
            ...svgTasks,
        ]);
    }

    _prepareLottieContainers() {
        document.querySelector('#homePage')?.classList.add('lottie-loading');
        this._setupLottieSceneLoaders();
    }

    _setupLottieSceneLoaders() {
        const scenes = [
            { container: '#explodedScene', section: '#explodedSection' },
            { container: '#fullyImmersiveScene', section: '#fullyImmersiveSection' },
        ];

        scenes.forEach(({ container, section }) => {
            const scene = document.querySelector(container);
            if (!scene) {
                return;
            }

            scene.classList.add('lottie-scene-pending');

            if (section) {
                document.querySelector(section)?.classList.add('lottie-scene-pending');
            }

            if (scene.querySelector('.lottie-scene-loading')) {
                return;
            }

            const loader = document.createElement('div');
            loader.className = 'lottie-scene-loading';
            loader.innerHTML = '<div class="spinner" role="status" aria-label="Loading animation"></div>';
            scene.appendChild(loader);
        });
    }

    _markLottieSceneReady(animation) {
        const config = {
            'exploded animation': { scene: '#explodedScene', section: '#explodedSection' },
            'fullyImmersive animation': { scene: '#fullyImmersiveScene', section: '#fullyImmersiveSection' },
        }[animation?.name];

        if (!config) {
            return;
        }

        const scene = document.querySelector(config.scene);
        if (scene) {
            scene.classList.remove('lottie-scene-pending');
            scene.classList.add('lottie-scene-ready');
            scene.querySelector('.lottie-scene-loading')?.remove();
        }

        if (config.section) {
            const sectionEl = document.querySelector(config.section);
            sectionEl?.classList.remove('lottie-scene-pending');
            sectionEl?.classList.add('lottie-scene-ready');
        }
    }

    _refreshLottieRender() {
        const animations = [
            this._eargasmAnimation,
            this._explodedAnimation,
            this._fullyImmersiveAnimation,
        ];

        animations.forEach((animation) => {
            if (!animation || animation.isLoaded !== true) return;

            animation.resize();

            if (animation.name === 'exploded animation') {
                animation.goToAndStop(114, true);
            }

            if (animation.name === 'fullyImmersive animation') {
                animation.goToAndStop(0, true);
            }
        });
    }

    /* Renders a Lottie animation at the frame requested by a scroll-scrubbed
     * GSAP tween.
     *
     * For the EXPLODED HEADPHONE animation the requested frame is snapped to
     * a coarse grid (every EXPLODED_FRAME_SNAP_STEP-th frame) so the canvas
     * is not re-rendered on every single scroll tick. `allowedFrames` is the
     * [min, max] range of the current explode phase (e.g. assembled -> minor
     * explosion) and the snapped frame is clamped inside it so a phase can
     * never overshoot into the next pose.
     *
     * NOTE: this used to snap to only the 3 key poses (0 / 82 / 114), which
     * made the headphone jump straight to "fully exploded" with no visible
     * in-between explosion. Snapping to a fine grid restores the gradual,
     * Live-site-like explode while keeping most of the performance win. */
    _goToLottieFrame(animation, frame, allowedFrames = null) {
        if (!animation || animation.isLoaded !== true) return;

        if (animation.name === 'exploded animation') {
            const range = allowedFrames || [EXPLODED_FIRST_FRAME, EXPLODED_LAST_FRAME];
            const minFrame = Math.min(...range);
            const maxFrame = Math.max(...range);

            // Snap the requested frame to the render grid, then clamp it to
            // the current explode phase's frame range.
            let snappedFrame = Math.round(frame / EXPLODED_FRAME_SNAP_STEP) * EXPLODED_FRAME_SNAP_STEP;
            snappedFrame = Math.min(Math.max(snappedFrame, minFrame), maxFrame);

            // Skip the (expensive) canvas render entirely when the snapped
            // frame is the same one that is already being displayed.
            if (this._lastExplodedFrame === snappedFrame) return;
            this._lastExplodedFrame = snappedFrame;

            animation.goToAndStop(snappedFrame, true);
            return;
        }

        animation.goToAndStop(frame, true);
    }

    _finalizeLottieAnimation(animation, frame) {
        if (!animation || !animation.name) {
            return;
        }

        if (this._readyLottieNames.has(animation.name)) {
            return;
        }

        animation.resize();
        animation.goToAndStop(frame, true);
        this._readyLottieNames.add(animation.name);
        this._markLottieSceneReady(animation);

        if (typeof ScrollTrigger !== 'undefined') {
            ScrollTrigger.refresh();
        }
    }

    _setupLottieReadyWait(animation, frame) {
        if (!animation) {
            return Promise.resolve();
        }

        if (animation._readyPromise) {
            return animation._readyPromise;
        }

        animation._readyPromise = new Promise((resolve) => {
            const settle = () => {
                this._finalizeLottieAnimation(animation, frame);
                requestAnimationFrame(() => {
                    requestAnimationFrame(resolve);
                });
            };

            const fail = () => {
                console.warn('[home1.js] Lottie failed or timed out:', animation.name);
                this._markLottieSceneReady(animation);
                resolve();
            };

            if (animation.isLoaded === true) {
                settle();
                return;
            }

            const timer = setTimeout(fail, CRITICAL_LOTTIE_MAX_MS);
            let dataReady = false;
            let settled = false;

            const trySettle = () => {
                if (!dataReady || settled) {
                    return;
                }

                settled = true;
                clearTimeout(timer);
                clearTimeout(domFallback);
                settle();
            };

            let domFallback = null;

            animation.addEventListener('data_ready', () => {
                dataReady = true;
                animation.resize();
                domFallback = setTimeout(trySettle, 400);
            }, { once: true });

            animation.addEventListener('DOMLoaded', trySettle, { once: true });

            animation.addEventListener('data_failed', () => {
                clearTimeout(timer);
                if (domFallback) {
                    clearTimeout(domFallback);
                }
                fail();
            }, { once: true });
        });

        return animation._readyPromise;
    }

    _waitForCriticalLottie() {
        const promises = [
            this._explodedReadyPromise,
            this._fullyImmersiveReadyPromise,
        ].filter(Boolean);

        if (!promises.length) {
            return Promise.resolve();
        }

        return Promise.all(promises);
    }

    _waitForHeroImage() {
        const hero = document.querySelector('#heroHeadphone img');
        if (!hero) {
            return Promise.resolve();
        }
        return this._waitForHtmlImage(hero);
    }

    _bindLottieResizeOnReady(animation, frame) {
        this._setupLottieReadyWait(animation, frame);
    }

    _startLottieAnimations() {
        if (typeof lottie === 'undefined') {
            return;
        }

        const eargasmScene = document.querySelector('#eargasmScene');
        if (eargasmScene) {
            this._eargasmAnimation = lottie.loadAnimation({
                container: eargasmScene,
                path: 'https://in.soniclamb.com/cdn/shop/t/6/assets/eargasmHeadphoneAnimation.json?v=63622552447361353591749294065',
                renderer: 'svg',
                loop: false,
                autoplay: false,
                name: 'eargasm animation',
                progressiveLoad: true,
                rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
            });
            this._bindLottieResizeOnReady(this._eargasmAnimation, 0);
        }

        const explodedScene = document.querySelector('#explodedScene');
        if (explodedScene) {
            this._explodedAnimation = lottie.loadAnimation({
                container: explodedScene,
                path: 'https://in.soniclamb.com/cdn/shop/t/6/assets/explodedAnimation.json?v=137885198500779133081749294066',
                renderer: 'canvas',
                loop: false,
                autoplay: false,
                name: 'exploded animation',
                progressiveLoad: false,
                rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
            });
            this._explodedReadyPromise = this._setupLottieReadyWait(this._explodedAnimation, 114);
        }

        const fullyImmersiveScene = document.querySelector('#fullyImmersiveScene');
        if (fullyImmersiveScene) {
            this._fullyImmersiveAnimation = lottie.loadAnimation({
                container: fullyImmersiveScene,
                path: 'https://in.soniclamb.com/cdn/shop/t/6/assets/fullyImmersiveAnimation.json?v=4013404519590825561749294195',
                renderer: 'canvas',
                loop: false,
                autoplay: false,
                name: 'fullyImmersive animation',
                progressiveLoad: false,
                rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
            });
            this._fullyImmersiveReadyPromise = this._setupLottieReadyWait(this._fullyImmersiveAnimation, 0);
        }
    }

    _areCriticalLottiesReady() {
        const required = [];
        if (this._explodedAnimation) {
            required.push('exploded animation');
        }
        if (this._fullyImmersiveAnimation) {
            required.push('fullyImmersive animation');
        }
        if (!required.length) {
            return true;
        }
        return required.every((name) => this._readyLottieNames.has(name));
    }

    async _whenPageReady() {
        await Promise.all([
            Promise.race([
                this._waitForHeroImage(),
                new Promise((resolve) => setTimeout(resolve, PAGE_LOADER_MAX_MS)),
            ]),
            this._waitForCriticalLottie(),
        ]);
    }

    _setupSectionLazyImages() {
        const sections = _getLazySections();
        if (!sections.length) {
            return;
        }

        const loadSection = (section) => {
            _loadLazyImagesIn(section);

            if (section.id !== 'otherFeaturesSection') {
                return;
            }

            requestAnimationFrame(() => {
                this._otherFeaturesImageCarousel?.update();
                this._otherFeaturesCarousel?.update();
            });
        };

        if (!('IntersectionObserver' in window)) {
            sections.forEach(loadSection);
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                loadSection(entry.target);
                observer.unobserve(entry.target);
            });
        }, { rootMargin: SECTION_LAZY_ROOT_MARGIN, threshold: 0 });

        sections.forEach((section) => observer.observe(section));
    }

    initialize() {
        document.documentElement.classList.add('home-page-loading');
        _deferHomePageImagesEarly();
        this._setupSectionLazyImages();
        this._prepareLottieContainers();
        this._startLottieAnimations();
        this._whenPageReady()
            .then(() => this._handleWindowLoad())
            .catch(() => this._handleWindowLoad());
        window.onresize = () => this._handleWindowResize();
    }

    _revealHomePage() {
        const homePage = document.querySelector('#homePage');
        if (!homePage) return;

        const heroLoading = document.querySelector('#heroLoading');
        if (heroLoading) {
            heroLoading.setAttribute('aria-busy', 'false');
        }

        homePage.classList.remove('lottie-loading');
        homePage.classList.add('loaded');
        document.documentElement.classList.remove('home-page-loading');
    }

    _playHeroAnimation() {
        gsap.timeline()
            .fromTo('#heroHeadphone img', { y: '-10%', autoAlpha: 0, scale: 0.9 }, { y: '0', scale: 1, autoAlpha: 1, duration: 0.3 })
            .fromTo('#heroText1', { autoAlpha: 0, y: "100%" }, { autoAlpha: 1, y: "0%", duration: 0.2 }, "-=0.2")
            .fromTo('#heroText2Wrapper .heroText2', { autoAlpha: 0, y: "50" }, { autoAlpha: 1, y: "0%", duration: 0.2 }, '-=0.1')
            .fromTo('#heroText2Wrapper .heroText2', { backgroundPosition: '100%' }, { backgroundPosition: '0%', ease: "linear", duration: 0.2 }, "-=0.1");
    }

    _identifyViewportSize() {
        const windowWidth = window.innerWidth;
        let viewportSize;

        if (windowWidth < 750) {
            viewportSize = ViewportSize.small;
        } else if (windowWidth < 990) {
            viewportSize = ViewportSize.medium;
        } else {
            viewportSize = ViewportSize.large;
        }

        return viewportSize;
    }

    _disableLottieGatedScrollTriggers() {
        if (typeof ScrollTrigger === 'undefined') {
            return;
        }

        LOTTIE_GATED_SCROLL_TRIGGER_IDS.forEach((id) => {
            ScrollTrigger.getById(id)?.disable();
        });
    }

    _enableLottieGatedScrollTriggers() {
        if (typeof ScrollTrigger === 'undefined') {
            return;
        }

        LOTTIE_GATED_SCROLL_TRIGGER_IDS.forEach((id) => {
            ScrollTrigger.getById(id)?.enable();
        });
        ScrollTrigger.refresh();
    }

    _handleWindowLoad() {
        if (this._windowLoadHandled) return;
        this._windowLoadHandled = true;

        this._initializeCarousels();
        this._initializeScrollspy();

        // TODO: uncomment once Zoho form is fixed
        // this._initializeZohoForm();

        requestAnimationFrame(() => {
            this._refreshLottieRender();
            requestAnimationFrame(() => {
                this._initializeScrollTimelines();
                this._initializeTrustBarCounters();

                if (this._areCriticalLottiesReady()) {
                    this._lottieGatedScrollEnabled = true;
                } else {
                    this._disableLottieGatedScrollTriggers();
                    this._initializeScrollWhenLottieReady();
                }

                if (this._areCriticalLottiesReady()) {
                    this._revealHomePage();
                    this._playHeroAnimation();
                } else {
                    this._waitForCriticalLottie().then(() => {
                        this._refreshLottieRender();
                        this._lottieGatedScrollEnabled = true;
                        this._enableLottieGatedScrollTriggers();
                        this._revealHomePage();
                        this._playHeroAnimation();
                    });
                }
            });
        });
    }

    _initializeScrollWhenLottieReady() {
        this._waitForCriticalLottie().then(() => {
            if (this._lottieGatedScrollEnabled) {
                return;
            }

            this._lottieGatedScrollEnabled = true;
            this._refreshLottieRender();
            this._enableLottieGatedScrollTriggers();
        });
    }

    _handleWindowResize() {
        // Reset other features carousel (only if it exists)
        if (this._otherFeaturesCarousel?.autoplay) {
            this._otherFeaturesCarousel.autoplay.stop();
        }

        // Resize lottie animations on window resize (only if they exist)
        // this._newBreedHeadphoneAnimation.resize();
        if (this._eargasmAnimation) this._eargasmAnimation.resize();
        if (this._explodedAnimation) this._explodedAnimation.resize();
        if (this._fullyImmersiveAnimation) this._fullyImmersiveAnimation.resize();

        // Reload timelines if window size type changes
        const newViewportSize = this._identifyViewportSize();
        if (newViewportSize != this._viewportSize) {
            console.log("<<< Old window type: ", this._viewportSize);
            console.log("<<< New window type: ", newViewportSize);
            this._viewportSize = newViewportSize;
            this._initializeScrollTimelines();
            this._initializeTrustBarCounters();
            if (this._lottieGatedScrollEnabled) {
                this._enableLottieGatedScrollTriggers();
            } else {
                this._disableLottieGatedScrollTriggers();
            }
        }
    }

    _initializeCarousels() {
        // Initialize reviewers carousel
        const reviewersCarousel = new Swiper('#reviewersCarousel', {
            direction: 'horizontal',
            loop: true,
            autoplay: {
                delay: 2500,
                disableOnInteraction: false,
            },
            spaceBetween: 30,
            slidesPerView: 2,
            breakpoints: {
                // when window width is >= 480px
                750: {
                    slidesPerView: 3,
                    spaceBetween: 30
                },
                // when window width is >= 640px
                920: {
                    slidesPerView: 4,
                    spaceBetween: 60
                }
            },
        });

        // Initialize other features carousel (only if elements exist)
        const startOtherFeaturesAutoPlayButton = document.querySelector("#startOtherFeaturesAutoPlayButton");
        const stopOtherFeaturesAutoPlayButton = document.querySelector("#stopOtherFeaturesAutoPlayButton");
        
        // Only initialize if the carousel elements exist
        if (startOtherFeaturesAutoPlayButton && stopOtherFeaturesAutoPlayButton) {
            startOtherFeaturesAutoPlayButton.style.display = "block";
            stopOtherFeaturesAutoPlayButton.style.display = "none";
        const otherFeaturesImageCarousel = new Swiper('#otherFeaturesImageCarousel', {
            loop: true,
            slidesPerView: 1,
            speed: this._viewportSize === ViewportSize.small ? 700 : 300,
            effect: "fade",
            fadeEffect: {
                crossFade: true,
            },
        });
        this._otherFeaturesImageCarousel = otherFeaturesImageCarousel;
        this._otherFeaturesCarousel = new Swiper('#otherFeaturesCarousel', {
            loop: true,
            spaceBetween: this._viewportSize === ViewportSize.small ? 0 : 50,
            slidesPerView: 1,
            speed: this._viewportSize === ViewportSize.small ? 700 : 300,
            allowTouchMove: this._viewportSize !== ViewportSize.small,
            autoplay: this._viewportSize === ViewportSize.small ? false : {
                enabled: false,
                delay: 5000,
            },
            autoHeight: true,
            pagination: {
                el: '#otherFeaturesPagination',
                clickable: true,
                renderBullet: (index, className) => {
                    const slideEls = document.querySelectorAll("#otherFeaturesCarousel .swiper-slide");
                    let slideLabel = `Item ${index}`;
                    if (index < slideEls.length) {
                        const slideEl = slideEls[index];
                        const label = slideEl.getAttribute("data-slide-label");
                        label && (slideLabel = label);
                    }

                    return `<div class="navItem ${className}">
                        <svg
                        class="indicator"
                        width="17"
                        height="16"
                        viewBox="0 0 17 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        >
                        <path d="M6.76795 15C7.53775 16.3333 9.46225 16.3333 10.2321 15L16.7272 3.75C17.497 2.41667 16.5348 0.75 14.9952 0.75L2.00481 0.75C0.46521 0.75 -0.497042 2.41667 0.272758 3.75L6.76795 15Z" fill="#F36416"/>
                        </svg>
                        <div class="label">${slideLabel}</div>
                    </div>`;
                }
            },
            on: {
                autoplayStart: () => {
                    startOtherFeaturesAutoPlayButton.style.display = "none";
                    stopOtherFeaturesAutoPlayButton.style.display = "block";
                },
                autoplayStop: () => {
                    startOtherFeaturesAutoPlayButton.style.display = "block";
                    stopOtherFeaturesAutoPlayButton.style.display = "none";
                },
            }
        });
        startOtherFeaturesAutoPlayButton.addEventListener("click", () => {
            this._otherFeaturesCarousel.autoplay.start();
        });
        stopOtherFeaturesAutoPlayButton.addEventListener("click", () => {
            this._otherFeaturesCarousel.autoplay.stop();
        });
            this._otherFeaturesCarousel.controller.control = otherFeaturesImageCarousel;
            otherFeaturesImageCarousel.controller.control = this._otherFeaturesCarousel;
            this._initializeOtherFeaturesScrollHint();
        } else {
            this._otherFeaturesCarousel = null;
            this._otherFeaturesImageCarousel = null;
        }
    }

    _initializeOtherFeaturesScrollHint() {
        const prevBtn = document.querySelector('#otherFeaturesScrollPrev');
        const nextBtn = document.querySelector('#otherFeaturesScrollNext');
        if (!prevBtn || !nextBtn || !this._otherFeaturesCarousel) return;

        prevBtn.addEventListener('click', () => {
            this._otherFeaturesCarousel.slidePrev();
            this._otherFeaturesScrollSlideIndex = this._otherFeaturesCarousel.realIndex;
            this._otherFeaturesCarousel.autoplay?.stop();
        });

        nextBtn.addEventListener('click', () => {
            this._otherFeaturesCarousel.slideNext();
            this._otherFeaturesScrollSlideIndex = this._otherFeaturesCarousel.realIndex;
            this._otherFeaturesCarousel.autoplay?.stop();
        });
    }

    _initializeScrollspy() {
        const menu = document.querySelector("#hyperPersonalizedModes .navButtons");
        const hyperPersonalizedScrollspy = new Scrollspy({ menu });
        hyperPersonalizedScrollspy.init();
    }

    _initializeZohoForm() {
        // We show zoho form on cursor leaving window only once.
        if (this._isZohoFormShown) {
            return;
        }

        const zohoFormModal = document.querySelector('zoho-form-modal');
        const handleMouseLeave = (event) => {
            zohoFormModal.show();

            // Mark as already shown
            this._isZohoFormShown = true;

            // Remove mouseleave handler
            document.removeEventListener("mouseleave", handleMouseLeave);
        }
        document.addEventListener("mouseleave", handleMouseLeave);
    }

    _initializeScrollTimelines() {

        // Kill any existing timelines (but preserve feature cards animation)
        let scrollTriggers = ScrollTrigger.getAll()
        for (const scrollTrigger of scrollTriggers) {
            // Don't kill the feature cards scroll trigger
            if (scrollTrigger.vars && scrollTrigger.vars.id !== 'featureCardsSticky') {
                scrollTrigger.kill(true)
            }
        }
        for (const timeline of this._scrollTimelines) {
            timeline.kill(true);
        }

        // Clear up any previous timelines
        this._scrollTimelines = [];

        // Intilaize animations
        console.log("<<< this._viewportSize: ", this._viewportSize)
        if (this._viewportSize === ViewportSize.small) {
            this._setupScrollTimelinesForSmallViewport();
        } else if (this._viewportSize === ViewportSize.medium) {
            this._setupScrollTimelinesForMediumViewport();
        } else {
            this._setupScrollTimelinesForLargeViewport();
        }

        this._addTrustBarScrollTimeline();
        this._addFeelMusicTitleScrollTimeline();

        this._resetTrustBarCounterState();

        // Preplay timelines
        this._prePlayTimelines();

        if (this._viewportSize === ViewportSize.small && window.scrollY < 120) {
            gsap.set('#heroText1Wrapper, #heroText2Wrapper, #heroText3Wrapper', {
                autoAlpha: 1,
                y: 0,
            });
            gsap.set('#heroText2Line1', { backgroundPosition: '0%' });
        }
    }

    _addTrustBarScrollTimeline() {
        const self = this;
        const isMobile = this._viewportSize === ViewportSize.small;
        const trustBar = document.querySelector('#trustBar');
        if (!trustBar) return;

        gsap.set('#trustBar', { autoAlpha: 0, y: 32 });

        const trustBarScroll = gsap.timeline({
            scrollTrigger: {
                id: 'trustBarScroll',
                trigger: '#trustBarSection',
                start: 'top 99%',
                endTrigger: '#feelMusicTitleSection',
                end: 'top top',
                scrub: 0.65,
                invalidateOnRefresh: true,
            },
        })
            .fromTo('#trustBar', {
                autoAlpha: 0,
                y: 32,
                immediateRender: false,
            }, {
                autoAlpha: 1,
                y: 0,
                ease: 'power1.out',
                duration: isMobile ? 0.28 : 0.2,
            }, 0)
            .fromTo('#trustBar', {
                autoAlpha: 1,
                y: 0,
                immediateRender: false,
            }, {
                autoAlpha: 0,
                y: isMobile ? -20 : -18,
                ease: 'power1.in',
                duration: isMobile ? 0.22 : 0.2,
            }, 0.98);

        trustBarScroll.eventCallback('onUpdate', function () {
            const progress = this.progress();
            const counterStart = isMobile ? 0.3 : 0.32;

            if (progress >= counterStart && !self._trustBarCountersPlayed) {
                self._trustBarCountersPlayed = true;
                self._playTrustBarCounters();
            }

            if (progress < 0.12) {
                self._trustBarCountersPlayed = false;
                self._resetTrustBarCounters();
            }
        });

        this._scrollTimelines.push(trustBarScroll);
    }

    _addFeelMusicTitleScrollTimeline() {
        const introLine = document.querySelector('.feelMusicTitleIntro');
        const accentLine = document.querySelector('.feelMusicTitleAccent');
        if (!introLine || !accentLine) return;

        const isMobile = this._viewportSize === ViewportSize.small;
        gsap.set('#feelMusicTitle', { autoAlpha: 1, y: 0 });
        gsap.set('.feelMusicTitleLine', {
            autoAlpha: 0,
            y: isMobile ? 18 : 24,
            filter: 'blur(5px)',
            backgroundPosition: '0%',
        });

        /* "What Makes You FEEL Music?" scroll choreography.
         *
         * PRIOR APPROACH (removed): this timeline used to ALSO own the
         * fade-out, timed as a hand-picked percentage (e.g. "88%") of its
         * own 225vh-equivalent scrub range (start:'top bottom' to end:
         * 'bottom top'). That range is a different total length than
         * #explodedSection's own scroll range, so the fade-out percentage
         * had to be guessed and re-guessed to try to land exactly when
         * #explodedSection's top reaches the viewport — guess too early and
         * you get a blank gap, guess too late (or hold too long) and the
         * title is still fully opaque while the headphone is already
         * fading in underneath it (the reported overlap), or the long
         * "hold" just reads as extra, content-free scrolling.
         *
         * FIX: stop guessing. This timeline now only handles fade-IN, then
         * simply holds at full opacity (no tween = no change) for the rest
         * of ITS OWN range. The fade-OUT lives in a separate timeline
         * (_addFeelMusicTitleFadeOutTimeline below) that is anchored
         * directly to '#explodedSection' — the SAME element whose 'top
         * top' edge is where the exploded-headphone timeline begins fading
         * IN. Because both the title's fade-out and the headphone's
         * fade-in are computed from that one shared anchor point, they are
         * guaranteed to meet at exactly the same scroll position: the
         * title finishes fading to 0 the instant the headphone starts
         * fading in from 0. Zero possible overlap, zero possible gap —
         * by construction, not by percentage-tuning. */
        const feelMusicTitleScroll = gsap.timeline({
            scrollTrigger: {
                id: 'feelMusicTitleScroll',
                trigger: '#feelMusicTitleSection',
                start: 'top bottom',
                end: 'bottom top',
                scrub: isMobile ? 0.55 : 0.65,
                invalidateOnRefresh: true,
            },
        })
            // Guard segment: keep the lines hidden for the very first few
            // percent (also restores the hidden state when scrolling back up).
            .to('.feelMusicTitleLine', {
                autoAlpha: 0,
                y: isMobile ? 18 : 24,
                filter: 'blur(5px)',
                ease: 'none',
                duration: 0.05,
            }, 0)
            // Fade/blur the two title lines in, staggered, while the section
            // is still travelling up from the bottom of the viewport.
            .fromTo('.feelMusicTitleLine', {
                autoAlpha: 0,
                y: isMobile ? 18 : 24,
                filter: 'blur(5px)',
                immediateRender: false,
            }, {
                autoAlpha: 1,
                y: 0,
                filter: 'blur(0px)',
                ease: 'power1.out',
                stagger: isMobile ? 0.04 : 0.05,
                duration: isMobile ? 0.18 : 0.2,
            }, isMobile ? 0.06 : 0.07)
            // Gradient shimmer sweep across the text once it has faded in.
            // Nothing after this touches autoAlpha, so the title simply
            // stays fully visible for the rest of this timeline's range —
            // the dedicated fade-out timeline below takes over later.
            .to('.feelMusicTitleLine', {
                backgroundPosition: '-55%',
                ease: 'none',
                duration: 0.14,
            }, isMobile ? 0.3 : 0.32)
            .to('.feelMusicTitleLine', {
                backgroundPosition: '0%',
                ease: 'none',
                duration: 0.12,
            }, isMobile ? 0.46 : 0.48);

        this._scrollTimelines.push(feelMusicTitleScroll);
        this._addFeelMusicTitleFadeOutTimeline(isMobile);
    }

    /* Fades "What Makes You FEEL Music?" out, anchored to #explodedSection
     * rather than to #feelMusicTitleSection (see the big comment above in
     * _addFeelMusicTitleScrollTimeline for why). This scrubs across the
     * 100vh immediately before #explodedSection's top reaches the viewport
     * top. It now owns BOTH sides of the handoff as a crossfade: the title
     * fades out while #explodedSceneWrapper fades in over the same window
     * (the exploded timeline's Phase 1 became a non-writing spacer), so
     * there is no stretch of blank white stage between the two. */
    _addFeelMusicTitleFadeOutTimeline(isMobile) {
        const explodedSection = document.querySelector('#explodedSection');
        if (!explodedSection) return;

        const titleFadeOut = gsap.timeline({
            scrollTrigger: {
                id: 'feelMusicTitleFadeOut',
                trigger: '#explodedSection',
                start: 'top bottom',
                end: 'top top',
                scrub: isMobile ? 0.45 : 0.55,
                invalidateOnRefresh: true,
            },
        })
            .fromTo('.feelMusicTitleLine', {
                autoAlpha: 1,
                y: 0,
                filter: 'blur(0px)',
                immediateRender: false,
            }, {
                autoAlpha: 0,
                y: isMobile ? -8 : -10,
                filter: 'blur(6px)',
                ease: 'power1.in',
                stagger: { each: isMobile ? 0.03 : 0.04, from: 'end' },
                duration: 1,
            })
            // Crossfade: while the title fades OUT above, the exploded scene
            // fades IN below, over this same 100vh approach window — so the
            // user never scrolls past a blank white stage before the pin.
            // (The exploded timeline's own Phase 1 is now just a spacer; this
            // is the scene's only fade-in writer until its Phase 6 fade-out.)
            .fromTo('#explodedSceneWrapper', {
                autoAlpha: 0,
                immediateRender: false,
            }, {
                autoAlpha: 1,
                ease: 'power1.inOut',
                duration: 1,
            }, 0);

        this._scrollTimelines.push(titleFadeOut);
    }

    _setupScrollTimelinesForSmallViewport() {
        const self = this;

        gsap.set('#heroText1Wrapper, #heroText2Wrapper, #heroText3Wrapper', {
            autoAlpha: 1,
            y: 0,
        });
        gsap.set('#heroText2Line1', { backgroundPosition: '0%' });

        const heroTrustTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'hero',
                trigger: '#heroSection',
                start: 'bottom 98%',
                endTrigger: '#trustBarSection',
                end: 'top 26%',
                scrub: 0.6,
                invalidateOnRefresh: true,
            },
        })
            .fromTo('#heroText2Wrapper', { autoAlpha: 1, y: 0, immediateRender: false }, { autoAlpha: 0, y: -55, ease: 'power1.out', duration: 0.75 }, 0.38)
            .fromTo('#heroText2Line1', { backgroundPosition: '0%' }, { backgroundPosition: '-60%', ease: 'linear', duration: 0.75 }, 0.38)
            .fromTo('#heroText1Wrapper', { autoAlpha: 1, y: 0, immediateRender: false }, { autoAlpha: 0, y: -32, duration: 0.7 }, 0.42)
            .fromTo('#heroText3Wrapper', { autoAlpha: 1, y: 0, immediateRender: false }, { autoAlpha: 0, y: -22, duration: 0.65 }, 0.44);
        this._scrollTimelines.push(heroTrustTimeline);

        // const reviewersTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'reviewersSection',
        //         trigger: '#reviewersSection',
        //         start: 'top 75%',
        //         end: '+=200',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo('#reviewersSection .title span', { autoAlpha: 0, y: 50 }, { autoAlpha: 1, y: 0, stagger: { each: 0.1 }, duration: 0.5 })
        //     .fromTo('#reviewersSection .background', { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5 }, "-=0.4")
        //     .fromTo('#reviewersCarousel', { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<");
        // this._scrollTimelines.push(reviewersTimeline);

        // const reviewTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'reviewSection',
        //         trigger: '#reviewSection',
        //         start: 'top 75%',
        //         end: '+=200',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo('#reviewText div > span', { autoAlpha: 0, y: 5 }, { autoAlpha: 1, y: 0, stagger: { each: 0.025 }, duration: 0.5 });
        // this._scrollTimelines.push(reviewTimeline);

        // const newBreedHeadphoneTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'newBreedHeadphonesSection',
        //         trigger: '#newBreedHeadphonesSection',
        //         start: 'top 70%',
        //         end: "top 5%",
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .to({ frame: 86 }, {
        //         frame: 0,
        //         duration: 0.5,
        //         onUpdate: function () {
        //             const frame = this.targets()[0].frame;
        //             self._newBreedHeadphoneAnimation.goToAndStop(frame, true);
        //         }
        //     })
        //     .to({ frame: 0 }, {
        //         frame: 86,
        //         duration: 0.5,
        //         onUpdate: function () {
        //             const frame = this.targets()[0].frame;
        //             self._newBreedHeadphoneAnimation.goToAndStop(frame, true);
        //         }
        //     });
        // this._scrollTimelines.push(newBreedHeadphoneTimeline);

        // const newBreedTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'newBreedSection',
        //         trigger: '#newBreedSection',
        //         start: 'top bottom',
        //         end: 'bottom bottom',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo('#orangeBackground', { autoAlpha: 0 }, { autoAlpha: 0.5, duration: 0.5 })
        //     .fromTo('#orangeBackground', { autoAlpha: 0.5, backgroundSize: "94.6vw 94.6vw" }, { autoAlpha: 1, backgroundSize: "300vw 300vw", ease: "power1.easeInOut", duration: 0.5 })
        //     .fromTo('#newBreedTexts', { y: 80 }, { y: 80, duration: 0.2 }, "<")
        //     .fromTo('#newBreedTitleLine1', { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.2 }, "<")
        //     .fromTo('#newBreedTitleLine1 .gradientText', { backgroundPosition: '100%' }, { backgroundPosition: '0%', duration: 0.5 }, "<")
        //     .fromTo('#newBreedTitleLine2', { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "-=0.5")
        //     .fromTo('#newBreedTexts', { y: 80 }, { y: 0, duration: 0.5 }, "<")
        //     .fromTo('#newBreedText1 div > span', { autoAlpha: 0, y: 5 }, { autoAlpha: 1, y: 0, stagger: { each: 0.025 }, duration: 0.5 }, "<")
        //     .fromTo('#newBreedTitleLine1 .gradientText', { backgroundPosition: '0%' }, { backgroundPosition: '100%', duration: 0.5 }, "<")
        //     .fromTo('#newBreedTitleLine1', { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -100, duration: 0.5, ease: "linear" }, "+=1.5")
        //     .fromTo('#newBreedTitleLine2', { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -70, duration: 0.5, ease: "linear" }, "<")
        //     .fromTo('#newBreedTextWrapper', { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -50, duration: 0.5, ease: "linear" }, "<")
        //     .fromTo('#orangeBackground', { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.6 }, "<");
        // this._scrollTimelines.push(newBreedTimeline);

        // const eargasmTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'eargasmSection',
        //         trigger: '#eargasmSection',
        //         start: 'top top',
        //         end: 'bottom bottom',
        //         scrub: 0,
        //         onEnter: () => {
        //             document.querySelector("#uspNavButton1").classList.add("active");
        //         },
        //         onLeaveBack: () => {
        //             document.querySelector("#uspNavButton1").classList.remove("active");
        //         },
        //         markers: false,
        //     },
        // })
        //     .fromTo("#eargasmScene", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.2 })
        //     .fromTo("#eargasmTexts", { autoAlpha: 0, scale: 0.95 }, { autoAlpha: 1, scale: 1, duration: 0.3 }, "<")
        //     .to({ frame: 0 }, {
        //         frame: 30,
        //         ease: "linear",
        //         duration: 0.2,
        //         onUpdate: function () {
        //             const frame = this.targets()[0].frame;
        //             self._eargasmAnimation.goToAndStop(frame, true);
        //         }
        //     }, "-=0.5")
        //     .fromTo("#eargasmTexts", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.1 })
        //     .to({ frame: 30 }, {
        //         frame: 111,
        //         ease: "linear",
        //         duration: 0.2,
        //         onUpdate: function () {
        //             const frame = this.targets()[0].frame;
        //             self._eargasmAnimation.goToAndStop(frame, true);
        //         }
        //     }, "<")
        //     .fromTo("#eargasmScene", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.1 });
        // this._scrollTimelines.push(eargasmTimeline);

        /* ====================================================================
         * EXPLODED HEADPHONE — MOBILE (small viewport) scroll timeline
         *
         * Scrubbed across the tall #explodedSection. Phases, in scroll order:
         *   1. Scene fades in (crossfading with the FEEL Music title above).
         *   2. INTRO COLLAPSE: Lottie plays 114 -> 0 (exploded -> assembled)
         *      while the wrapper settles into its centered resting position.
         *   3. MINOR EXPLOSION: Lottie plays 0 -> 82; ear pad and driver
         *      assembly visibly separate step by step (gradual, not a jump).
         *   4. Driver labels shown one after the other (sequential on mobile
         *      because the two labels would overlap on a narrow screen).
         *   5. FULL EXPLOSION: Lottie plays 82 -> 114, all parts spread out.
         *   6. Scene fades out and hands over to the next section.
         *
         * NOTE: an earlier pass zoomed the resting pose to y:-6vh / scale:1.0
         * to cut mobile whitespace, but on this 177.77vh-wide scene (see
         * #explodedScene in home.css) that pushed parts of the headphone
         * outside the #explodedContent overflow:hidden stage and clipped
         * it (ear cup / driver edges cut off). Reverted to the original,
         * non-clipping y:-22vh / scale:0.8 resting pose.
         *
         * ASPECT-RATIO BUG (separate from the zoom above): #explodedScene is
         * sized 177.77vh x 100vh so it always fully covers the viewport
         * (like CSS background-size:cover) — the box height always equals
         * 100vh, and its width is derived to keep the Lottie's native 16:9
         * ratio. Under that "cover" math, the FRACTION of the animation's
         * width that ends up visible on screen is proportional to the
         * viewport's own aspect ratio (viewportWidth / viewportHeight):
         * taller/narrower phones must show more of the box's height, which
         * forces a narrower horizontal slice into view. iPhone SE (375x896,
         * aspect ~0.56) is what the fixed 0.8 resting scale above was tuned
         * against; taller modern phones (iPhone XR 414x896 ~0.46, Pixel 7
         * 412x915 ~0.45) get a visibly narrower slice at that SAME scale,
         * which crops the hinge/ear-cushion at the edges — exactly the
         * clipping reported on those devices while SE looked fine.
         * Fix: scale every explode pose DOWN in proportion to how much
         * narrower the device's aspect ratio is than the SE reference, so
         * the same absolute framing width stays visible on every phone
         * (clamped so it never zooms IN past the SE-tuned baseline, and
         * never shrinks below 72% so extreme aspect ratios don't make the
         * headphone tiny). */
        const EXPLODED_MOBILE_REFERENCE_ASPECT = 375 / 667; // iPhone SE — the aspect ratio these poses were tuned against
        const explodedAspectScale = Math.max(
            0.72,
            Math.min(1, (window.innerWidth / window.innerHeight) / EXPLODED_MOBILE_REFERENCE_ASPECT),
        );
        const explodedIntroScale = 1.35 * explodedAspectScale;
        const explodedRestScale = 0.8 * explodedAspectScale;
        const explodedFadeOutScale = 0.9 * explodedAspectScale;

        const explodedTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'explodedSection',
                trigger: '#explodedSection',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                markers: false,
            },
        })
            // Phase 1 — spacer only. The scene's fade-in was moved out of the
            // pinned range: it now crossfades with the FEEL Music title during
            // the 100vh BEFORE the pin (see _addFeelMusicTitleFadeOutTimeline),
            // so the stage is never blank. This empty tween must NOT touch
            // autoAlpha (two writers would fight/flash); it only preserves the
            // original scroll-length proportions of the later phases.
            .to({}, { duration: 0.3 })
            // Phase 2 — intro collapse: fully exploded (114) -> assembled (0).
            .to({ frame: 114 }, {
                frame: 0,
                ease: "power1.easeOut",
                duration: 1.1,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._explodedAnimation, frame, [EXPLODED_FIRST_FRAME, EXPLODED_LAST_FRAME]);
                }
            })
            // Wrapper glides from its large intro pose into the centered
            // resting pose ("<" = starts together with the collapse).
            .fromTo("#explodedSceneWrapper", { x: "-3vh", y: "-13vh", scale: explodedIntroScale }, { x: "0vh", y: "-22vh", scale: explodedRestScale, duration: 1.1 }, "<")
            // Intro texts (kept for compatibility; section markup currently
            // has #explodedTexts commented out).
            .fromTo("#explodedTexts", { autoAlpha: 0, scale: 0.9 }, { autoAlpha: 1, scale: 1, duration: 0.3 }, "-=0.5")
            .fromTo("#explodedTexts", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 }, "+=0.25")
            // Phase 3 — minor explosion: assembled (0) -> mid pose (82).
            // Longer duration = more scroll distance = readable, gradual explode.
            .to({ frame: 0 }, {
                frame: EXPLODED_MIDDLE_FRAME,
                ease: "power1.easeIn",
                duration: 0.5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._explodedAnimation, frame, [EXPLODED_FIRST_FRAME, EXPLODED_MIDDLE_FRAME]);
                }
            })
            // Phase 4 — labels, one at a time (Dynamic Driver, then Impulse
            // Driver); each is held on screen for a stretch of scroll.
            .fromTo("#explodedLabel1", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3 })
            .fromTo("#explodedLabel1", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 }, "+=0.6")
            .fromTo("#explodedLabel2", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3 })
            .fromTo("#explodedLabel2", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 }, "+=0.6")
            // Phase 5 — full explosion: mid pose (82) -> fully exploded (114).
            .to({ frame: EXPLODED_MIDDLE_FRAME }, {
                frame: 114,
                ease: "power1.easeInOut",
                duration: 0.5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._explodedAnimation, frame, [EXPLODED_MIDDLE_FRAME, EXPLODED_LAST_FRAME]);
                }
            }, "+=0.35")
            // Phase 6 — fade the scene out.
            .fromTo("#explodedSceneWrapper", { autoAlpha: 1, scale: explodedRestScale }, { autoAlpha: 0, scale: explodedFadeOutScale, duration: 0.3 });
        this._scrollTimelines.push(explodedTimeline);

        const fullyImmersiveTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'fullyImmersiveSection',
                trigger: '#fullyImmersiveSection',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                onEnter: () => {
                    // document.querySelector("#uspNavButton1").classList.remove("active");
                    document.querySelector("#uspNavButton2").classList.add("active");
                },
                onLeaveBack: () => {
                    // document.querySelector("#uspNavButton1").classList.add("active");
                    document.querySelector("#uspNavButton2").classList.remove("active");
                },
                markers: false,
            },
        })
            .fromTo("#fullyImmersiveScene", { y: "100%" }, { y: "0%", ease: "linear", duration: 1 })
            .fromTo("#fullyImmersiveScene", { scale: 0.9 }, { scale: 1, ease: "linear", duration: 1 })
            .to({ frame: 1 }, {
                frame: 29,
                duration: 1,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#fullyImmersiveText1", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 })
            .to({ frame: 29 }, {
                frame: 89,
                duration: 1.1,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            }, "<")
            .to("#fullyImmersiveScene", {
                onStart: function () {
                    document.querySelector("#fullyImmersiveScene").classList.add("topAligned");
                },
                onReverseComplete: function () {
                    document.querySelector("#fullyImmersiveScene").classList.remove("topAligned");
                },
            })
            .fromTo("#fullyImmersiveText1", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -30, duration: 0.5 }, "-=0.5")
            .fromTo("#fullyImmersiveText2", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 })
            .to({ frame: 89 }, {
                frame: 110,
                duration: 1,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#fullyImmersiveText2", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 })
            .to({ frame: 110 }, {
                frame: 321,
                duration: 5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            }, "<")
            .fromTo("#fullyImmersiveScene", { x: 0, y: "0%" }, { x: 0, y: "0%", ease: "linear", duration: 0.3 })
            .to({ frame: 321 }, {
                // Clamped from 339: source frames ~332-337 of the JPG sequence
                // crop the mode dial at the bottom of the render, so the scrub
                // stops before them (scene is fading out here anyway).
                frame: 330,
                duration: 1,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            }, "<")
            .fromTo("#fullyImmersiveScene", { autoAlpha: 1 }, { autoAlpha: 0, ease: "linear", duration: 1 }, "<")
            .fromTo("#fullyImmersiveText3 p", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 })
            .fromTo("#fullyImmersiveText3 h3", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "-=0.25")
            .fromTo("#fullyImmersiveText3 blockquote", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 })
            .fromTo("#fullyImmersiveText3", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -30, duration: 0.5 }, "+=0.5")
            .fromTo("#fullyImmersiveScene", { autoAlpha: 0 }, { autoAlpha: 1, ease: "linear", duration: 0.5 }, "<");
        this._scrollTimelines.push(fullyImmersiveTimeline);

        const hyperPersonalizedIntroTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'hyperPersonalizedIntroTimeline',
                trigger: '#hyperPersonalizedIntroTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                onEnter: () => {
                    document.querySelector("#uspNavButton2").classList.remove("active");
                    document.querySelector("#uspNavButton3").classList.add("active");
                },
                onLeaveBack: () => {
                    document.querySelector("#uspNavButton2").classList.add("active");
                    document.querySelector("#uspNavButton3").classList.remove("active");
                },
                markers: false,
            },
        })
            .to({ frame: 339 }, {
                frame: 354,
                duration: 0.5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#fullyImmersiveScene", { y: 0, scale: 1 }, { y: "-35%", scale: 1, ease: "linear", duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedIntroContent h3", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedIntroContent p", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "-=0.5")
            .fromTo("#hyperPersonalizedIntroContent", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -30, duration: 0.5 }, "+=0.5");
        this._scrollTimelines.push(hyperPersonalizedIntroTimeline);

        const hearTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'hearTimeline',
                trigger: '#hearTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                toggleClass: {
                    targets: "#hyperPersonalizedModesNavButton1",
                    className: "active",
                },
                markers: false,
            },
        })
            .fromTo("#fullyImmersiveScene", { y: "-35%" }, { y: "-50%", ease: "linear", duration: 0.5 })
            .fromTo("#hyperPersonalizedModes .navButtons", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #hearContent", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "-=0.5")
            .fromTo("#hyperPersonalizedModes #hearContent", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "+=1");
        this._scrollTimelines.push(hearTimeline);

        const feelTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'feelTimeline',
                trigger: '#feelTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                toggleClass: {
                    targets: "#hyperPersonalizedModesNavButton2",
                    className: "active",
                },
                markers: false,
            },
        })
            .to({ frame: 354 }, {
                frame: 365,
                duration: 0.3,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#hyperPersonalizedModes #feelContent", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #feelContent", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "+=1");
        this._scrollTimelines.push(feelTimeline);

        const immerseTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'immerseTimeline',
                trigger: '#immerseTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                toggleClass: {
                    targets: "#hyperPersonalizedModesNavButton3",
                    className: "active",
                },
                markers: false,
            },
        })
            .to({ frame: 380 }, {
                frame: 390,
                duration: 0.3,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#hyperPersonalizedModes #immerseContent", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #immerseContent", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "+=1");
        this._scrollTimelines.push(immerseTimeline);

        const beastTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'beastTimeline',
                trigger: '#beastTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                toggleClass: {
                    targets: "#hyperPersonalizedModesNavButton4",
                    className: "active",
                },
                markers: false,
            },
        })
            .to({ frame: 405 }, {
                frame: 415,
                duration: 0.3,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#hyperPersonalizedModes #beastContent", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #beastContent", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "+=1")
            .fromTo("#hyperPersonalizedModes .navButtons", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "<")
            .fromTo("#fullyImmersiveScene", { autoAlpha: 1, y: "-50%" }, { autoAlpha: 0, y: "-65%", duration: 0.5 }, "<")
            // .fromTo("#uspNavButtons", { alautoAlphapha: 1 }, { autoAlpha: 0, duration: 0.5 }, "<");
        this._scrollTimelines.push(beastTimeline);

        // const earPrintTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'earprintSection',
        //         trigger: '#earprintSection',
        //         start: 'top top',
        //         end: 'bottom bottom',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo("#earprintImage", { autoAlpha: 0 }, { autoAlpha: 1, ease: "linear", duration: 0.3 })
        //     .from("#earprintImage .curve", { duration: 0.5, drawSVG: "100% live" })
        //     .fromTo("#earprintPhone", { autoAlpha: 0, scale: 10 }, { autoAlpha: 1, scale: 1, ease: "linear", duration: 0.5 })
        //     .fromTo("#earprintImage", { scale: 1.4 }, { scale: 0.8, ease: "linear", duration: 0.5 }, "<")
        //     .fromTo("#earprintImages .wrapper .wrapperInner", { y: "0%" }, { y: "-17%", ease: "linear", duration: 0.5 }, "-=0.1")
        //     .fromTo("#earprintTexts .wrapper", { autoAlpha: 0, x: 0, y: 30 }, { autoAlpha: 1, x: 0, y: 0, duration: 0.5 }, "-=0.5")
        //     .fromTo("#earprintImages, #earprintTexts .wrapper", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 }, "+=1");
        // this._scrollTimelines.push(earPrintTimeline);

        const aboveAndBeyondTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'aboveAndBeyondSection',
                trigger: '#aboveAndBeyondSection',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                markers: false,
            },
        })
            .fromTo("#aboveAndBeyondContent .title", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 })
            .fromTo("#aboveAndBeyondContent .title", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -30, duration: 0.5 }, "+=1");
        this._scrollTimelines.push(aboveAndBeyondTimeline);

        gsap.set("#otherFeaturesCarousel, #otherFeaturesSection .imageArea, #otherFeaturesContent .toolbar", { x: 0 });

        const otherFeaturesTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'otherFeaturesSection',
                trigger: '#otherFeaturesSection',
                start: 'top top',
                end: 'bottom bottom',
                scrub: true,
                snap: {
                    snapTo: 1 / 3,
                    duration: { min: 0.35, max: 0.65 },
                    delay: 0.15,
                    ease: 'power2.inOut',
                },
                markers: false,
                onUpdate: (scrollSelf) => {
                    if (Math.abs(scrollSelf.getVelocity()) > 5) return;

                    this._setOtherFeaturesSlideFromScroll(scrollSelf.progress);

                    if (scrollSelf.progress <= 0.01 || scrollSelf.direction === 1) {
                        gsap.to("#otherFeaturesSection", {
                            autoAlpha: 1,
                            duration: 0.3,
                            ease: "power2.out"
                        });
                    }
                },
                onLeave: () => {
                    this._otherFeaturesCarousel?.autoplay?.stop();
                },
                onLeaveBack: () => {
                    this._otherFeaturesScrollSlideIndex = -1;
                    this._otherFeaturesCarousel?.autoplay?.stop();
                    gsap.set("#otherFeaturesCarousel, #otherFeaturesSection .imageArea", { x: 0 });
                    gsap.to("#aboveAndBeyondSection", {
                        autoAlpha: 1,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                    gsap.to("#otherFeaturesSection", {
                        autoAlpha: 0,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                },
                onEnterBack: () => {
                    this._otherFeaturesCarousel?.autoplay?.stop();
                    gsap.set("#otherFeaturesCarousel, #otherFeaturesSection .imageArea", { x: 0 });
                    gsap.to("#aboveAndBeyondSection", {
                        autoAlpha: 0,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                    gsap.to("#otherFeaturesSection", {
                        autoAlpha: 1,
                        duration: 0.3,
                        ease: "power2.out"
                    });
                },
                onEnter: () => {
                    this._otherFeaturesScrollSlideIndex = -1;
                    this._otherFeaturesCarousel?.autoplay?.stop();
                    gsap.set("#otherFeaturesCarousel, #otherFeaturesSection .imageArea", { x: 0 });
                    gsap.to("#aboveAndBeyondSection", {
                        autoAlpha: 0,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                    gsap.to("#otherFeaturesSection", {
                        autoAlpha: 1,
                        duration: 0.3,
                        ease: "power2.out"
                    });
                },
            },
        });

        if (this._otherFeaturesCarousel) {
            otherFeaturesTimeline
                .to("#otherFeaturesCarousel", {
                    duration: 0.0001,
                    onStart: () => {
                        this._otherFeaturesCarousel.slideTo(0);
                        this._otherFeaturesCarousel?.autoplay?.stop();
                    },
                    onReverseComplete: () => {
                        this._otherFeaturesCarousel?.autoplay?.stop();
                        this._otherFeaturesCarousel.slideTo(0);
                    },
                });
        }

        this._scrollTimelines.push(otherFeaturesTimeline);
    }

    _setupScrollTimelinesForMediumViewport() {
        const self = this;

        const heroTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'hero',
                trigger: 'body',
                start: 'top top',
                end: 'bottom bottom',
                endTrigger: '#heroSection',
                scrub: 0,
                markers: false,
            },
        })
            .fromTo('#heroHeadphone', { scale: 1 }, { scale: 2.1, ease: 'linear', duration: 0.5 })
            .fromTo('#heroTexts', { scale: 1 }, { scale: 0.9, duration: 0.5 }, '<')
            .fromTo('#heroText2Line1', { backgroundPosition: '0%' }, { backgroundPosition: '-60%', ease: 'linear', duration: 0.5 }, '<')
            .fromTo('#heroHeadphone', { scale: 2.1 }, { scale: 3.24, ease: 'linear', duration: 0.3 })
            .fromTo('#heroText2Line1', { backgroundPosition: '-60%' }, { backgroundPosition: '0%', ease: 'linear', duration: 0.3 }, '<')
            .fromTo('#heroHeadphone', { scale: 3.24 }, { scale: 8, ease: 'linear', duration: 0.5 })
            .fromTo('#heroText2Line1', { backgroundPosition: '0%' }, { backgroundPosition: '-100%', duration: 0.5 }, '<')
//           .fromTo('#heroText1Wrapper', 
//     { autoAlpha: 1, y: 0, scale: 1 }, 
//     { autoAlpha: 0, y: -100, scale: 0.9, duration: 0.5 }, '<')
// .fromTo('#heroText2Wrapper', 
//     { autoAlpha: 1, y: 0 }, 
//     { autoAlpha: 0, y: -100, duration: 0.5 }, '<')
// .fromTo('#heroText3Wrapper', 
//     { autoAlpha: 0, scale: 0.9, y: 100 }, 
//     { autoAlpha: 1, scale: 1, y: 0, duration: 0.5 }, '<')

            .fromTo('#heroText1Wrapper', { autoAlpha: 1, scale: 1, y: 0 }, { autoAlpha: 0, scale: 0.9, y: -80, duration: 0.5 }, '<')
            .fromTo('#heroText2Wrapper', { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -100, duration: 0.5 }, '<')
            // .fromTo('#heroText3Wrapper', { autoAlpha: 1, scale: 1, y: 0 }, { autoAlpha: 0, scale: 0.9, y: -100, duration: 0.5 }, '-=0.9')
            .fromTo('#heroHeadphone', { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.000000001 });
        this._scrollTimelines.push(heroTimeline);

        // const reviewersTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'reviewersSection',
        //         trigger: '#reviewersSection',
        //         start: 'top 75%',
        //         end: '+=200',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo('#reviewersSection .title span', { autoAlpha: 0, y: 50 }, { autoAlpha: 1, y: 0, stagger: { each: 0.1 }, duration: 0.5 })
        //     .fromTo('#reviewersSection .background', { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5 }, "-=0.4")
        //     .fromTo('#reviewersCarousel', { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<");
        // this._scrollTimelines.push(reviewersTimeline);

        // const reviewTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'reviewSection',
        //         trigger: '#reviewSection',
        //         start: 'top 75%',
        //         end: '+=200',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo('#reviewText div > span', { autoAlpha: 0, y: 5 }, { autoAlpha: 1, y: 0, stagger: { each: 0.025 }, duration: 0.5 });
        // this._scrollTimelines.push(reviewTimeline);

        // const newBreedHeadphoneTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'newBreedHeadphonesSection',
        //         trigger: '#newBreedHeadphonesSection',
        //         start: 'top 70%',
        //         end: 'bottom bottom',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo('#newBreedHeadphonesScene', { y: "5%" }, { y: "0%", duration: 0.81 })
        //     .to({ frame: 149 }, {
        //         frame: 0,
        //         duration: 0.5,
        //         onUpdate: function () {
        //             const frame = this.targets()[0].frame;
        //             self._newBreedHeadphoneAnimation.goToAndStop(frame, true);
        //         }
        //     })
        //     .to({ frame: 0 }, {
        //         frame: 149,
        //         duration: 0.5,
        //         onUpdate: function () {
        //             const frame = this.targets()[0].frame;
        //             self._newBreedHeadphoneAnimation.goToAndStop(frame, true);
        //         }
        //     });
        // this._scrollTimelines.push(newBreedHeadphoneTimeline);

        // const newBreedTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'newBreedSection',
        //         trigger: '#newBreedSection',
        //         // start: 'top top',
        //         start: 'top bottom',
        //         end: 'bottom bottom',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo('#orangeBackground', { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3 })
        //     .fromTo('#newBreedTexts', { y: 80 }, { y: 80, duration: 0.2 }, "<")
        //     .fromTo('#newBreedTitleLine1', { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.2 }, "<")
        //     .fromTo('#newBreedTitleLine1 .gradientText', { backgroundPosition: '100%' }, { backgroundPosition: '0%', duration: 0.5 }, "<")
        //     .fromTo('#newBreedTitleLine2', { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "-=0.5")
        //     .fromTo('#orangeBackground', { backgroundSize: "94.6vw 94.6vw" }, { backgroundSize: "120vw 120vw", ease: "power1.easeInOut", duration: 0.5 })
        //     .fromTo('#newBreedTexts', { y: 80 }, { y: 0, duration: 0.5 }, "<")
        //     .fromTo('#newBreedText1 div > span', { autoAlpha: 0, y: 5 }, { autoAlpha: 1, y: 0, stagger: { each: 0.025 }, duration: 0.5 }, "<")
        //     .fromTo('#newBreedTitleLine1 .gradientText', { backgroundPosition: '0%' }, { backgroundPosition: '100%', duration: 0.5 }, "<")
        //     .fromTo('#orangeBackground', { backgroundSize: "100vw 100vw" }, { backgroundSize: "200vw 200vw", ease: "power1.easeInOut", duration: 0.5 }, "+=0.5")
        //     .fromTo('#newBreedTitleLine1 .gradientText', { backgroundPosition: '100%' }, { backgroundPosition: '0%', duration: 0.5 }, "<")
        //     .fromTo('#newBreedTitleLine1', { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -100, duration: 0.5, ease: "linear" }, "+=0.5")
        //     .fromTo('#newBreedTitleLine2', { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -70, duration: 0.5, ease: "linear" }, "<")
        //     .fromTo('#newBreedTextWrapper', { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -50, duration: 0.5, ease: "linear" }, "<")
        //     .fromTo('#orangeBackground', { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.6 }, "<");
        // this._scrollTimelines.push(newBreedTimeline);

        // const eargasmTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'eargasmSection',
        //         trigger: '#eargasmSection',
        //         start: 'top top',
        //         end: 'bottom bottom',
        //         scrub: 0,
        //         onEnter: () => {
        //             document.querySelector("#uspNavButton1").classList.add("active");
        //         },
        //         onLeaveBack: () => {
        //             document.querySelector("#uspNavButton1").classList.remove("active");
        //         },
        //         markers: false,
        //     },
        // })
        //     .fromTo("#eargasmScene", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.2 })
        //     .fromTo("#eargasmTexts", { autoAlpha: 0, scale: 0.95 }, { autoAlpha: 1, scale: 1, duration: 0.3 }, "<")
        //     .to({ frame: 0 }, {
        //         frame: 30,
        //         ease: "linear",
        //         duration: 0.2,
        //         onUpdate: function () {
        //             const frame = this.targets()[0].frame;
        //             self._eargasmAnimation.goToAndStop(frame, true);
        //         }
        //     }, "-=0.5")
        //     .fromTo("#eargasmTexts", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.2 })
        //     .to({ frame: 30 }, {
        //         frame: 111,
        //         ease: "linear",
        //         duration: 0.2,
        //         onUpdate: function () {
        //             const frame = this.targets()[0].frame;
        //             self._eargasmAnimation.goToAndStop(frame, true);
        //         }
        //     }, "<")
        //     .fromTo("#eargasmScene", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.1 });
        // this._scrollTimelines.push(eargasmTimeline);

        /* ====================================================================
         * EXPLODED HEADPHONE — TABLET (medium viewport) scroll timeline
         *
         * Same phase structure as mobile (see the small-viewport timeline for
         * the full phase-by-phase description), with two differences:
         *   - Both driver labels are shown TOGETHER (enough screen width),
         *     which keeps the total scroll shorter — retained by design.
         * NOTE: an earlier pass zoomed the resting pose to scale 1.15 /
         * y:-16vh to cut whitespace, but that pushed parts of the headphone
         * outside the #explodedContent overflow:hidden stage and clipped
         * it. Reverted to the original, non-clipping y:-22vh / scale:1
         * resting pose.
         * ==================================================================*/
        const explodedTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'explodedSection',
                trigger: '#explodedSection',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                markers: false,
            },
        })
            // Phase 1 — spacer only. The scene's fade-in was moved out of the
            // pinned range: it now crossfades with the FEEL Music title during
            // the 100vh BEFORE the pin (see _addFeelMusicTitleFadeOutTimeline),
            // so the stage is never blank. This empty tween must NOT touch
            // autoAlpha (two writers would fight/flash); it only preserves the
            // original scroll-length proportions of the later phases.
            .to({}, { duration: 0.3 })
            // Phase 2 — intro collapse: fully exploded (114) -> assembled (0).
            .to({ frame: 114 }, {
                frame: 0,
                ease: "power1.easeOut",
                duration: 1.1,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._explodedAnimation, frame, [EXPLODED_FIRST_FRAME, EXPLODED_LAST_FRAME]);
                }
            })
            // Wrapper glides from the large intro pose into the resting
            // pose ("<" = starts together with the collapse).
            .fromTo("#explodedSceneWrapper", { x: "-3vh", y: "-13vh", scale: 1.35 }, { x: "0vh", y: "-22vh", scale: 1, duration: 1.1 }, "<")
            // Intro texts (kept for compatibility; section markup currently
            // has #explodedTexts commented out).
            .fromTo("#explodedTexts", { autoAlpha: 0, scale: 0.9 }, { autoAlpha: 1, scale: 1, duration: 0.3 }, "-=0.5")
            .fromTo("#explodedTexts", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 }, "+=0.25")
            // Phase 3 — minor explosion: assembled (0) -> mid pose (82).
            // Longer duration = more scroll distance = readable, gradual explode.
            .to({ frame: 0 }, {
                frame: EXPLODED_MIDDLE_FRAME,
                ease: "power1.easeIn",
                duration: 0.5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._explodedAnimation, frame, [EXPLODED_FIRST_FRAME, EXPLODED_MIDDLE_FRAME]);
                }
            })
            // Phase 4 — BOTH driver labels appear together ("<" = same time),
            // are held on screen, then fade out together.
            .fromTo("#explodedLabel1", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3 })
            .fromTo("#explodedLabel2", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3 }, "<")
            .fromTo("#explodedLabel1", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 }, "+=0.9")
            .fromTo("#explodedLabel2", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 }, "<")
            // Phase 5 — full explosion: mid pose (82) -> fully exploded (114).
            .to({ frame: EXPLODED_MIDDLE_FRAME }, {
                frame: 114,
                ease: "power1.easeInOut",
                duration: 0.5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._explodedAnimation, frame, [EXPLODED_MIDDLE_FRAME, EXPLODED_LAST_FRAME]);
                }
            }, "+=0.35")
            // Phase 6 — fade the scene out.
            .fromTo("#explodedSceneWrapper", { autoAlpha: 1, scale: 1 }, { autoAlpha: 0, scale: 0.9, duration: 0.3 });
        this._scrollTimelines.push(explodedTimeline);

        const fullyImmersiveTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'fullyImmersiveSection',
                trigger: '#fullyImmersiveSection',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                onEnter: () => {
                    // document.querySelector("#uspNavButton1").classList.remove("active");
                    document.querySelector("#uspNavButton2").classList.add("active");
                },
                onLeaveBack: () => {
                    // document.querySelector("#uspNavButton1").classList.add("active");
                    document.querySelector("#uspNavButton2").classList.remove("active");
                },
                markers: false,
            },
        })
            .fromTo("#fullyImmersiveScene", { y: "100%" }, { y: "0%", ease: "linear", duration: 0.5 })
            .fromTo("#fullyImmersiveScene", { scale: 0.9 }, { scale: 1, ease: "linear", duration: 0.5 })
            .to({ frame: 1 }, {
                frame: 29,
                duration: 1,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#fullyImmersiveText1", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 })
            .to({ frame: 29 }, {
                frame: 89,
                duration: 1.1,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            }, "<")
            .to("#fullyImmersiveScene", {
                onStart: function () {
                    document.querySelector("#fullyImmersiveScene").classList.add("topAligned");
                },
                onReverseComplete: function () {
                    document.querySelector("#fullyImmersiveScene").classList.remove("topAligned");
                },
            })
            .fromTo("#fullyImmersiveText1", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -30, duration: 0.5 }, "-=0.5")
            .fromTo("#fullyImmersiveText2", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 })
            .to({ frame: 89 }, {
                frame: 110,
                duration: 1,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#fullyImmersiveText2", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 })
            .to({ frame: 110 }, {
                frame: 321,
                duration: 4.5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            }, "<")
            .fromTo("#fullyImmersiveScene", { scale: 1 }, { scale: 0.55, ease: "linear", duration: 0.5 })
            .to({ frame: 321 }, {
                // Clamped from 339: source frames ~332-337 of the JPG sequence
                // crop the mode dial at the bottom of the render, so the scrub
                // stops before them (scene is fading out here anyway).
                frame: 330,
                duration: 0.5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            }, "<")
            .fromTo("#fullyImmersiveText3 p", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 })
            .fromTo("#fullyImmersiveText3 h3", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5}, "-=0.25")
            .fromTo("#fullyImmersiveText3 blockquote", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 })
            .fromTo("#fullyImmersiveText3", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -30, duration: 0.5 }, "+=0.5");
        this._scrollTimelines.push(fullyImmersiveTimeline);

        const hyperPersonalizedIntroTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'hyperPersonalizedIntroTimeline',
                trigger: '#hyperPersonalizedIntroTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                onEnter: () => {
                    document.querySelector("#uspNavButton2").classList.remove("active");
                    document.querySelector("#uspNavButton3").classList.add("active");
                },
                onLeaveBack: () => {
                    document.querySelector("#uspNavButton2").classList.add("active");
                    document.querySelector("#uspNavButton3").classList.remove("active");
                },
                markers: false,
            },
        })
            .to({ frame: 339 }, {
                frame: 354,
                duration: 0.5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#fullyImmersiveScene", { scale: 0.55 }, { scale: 0.6, ease: "linear", duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedIntroContent h3", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedIntroContent p", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "-=0.5")
            .fromTo("#hyperPersonalizedIntroContent", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -30, duration: 0.5 }, "+=0.5");
        this._scrollTimelines.push(hyperPersonalizedIntroTimeline);

        const hearTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'hearTimeline',
                trigger: '#hearTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                toggleClass: {
                    targets: "#hyperPersonalizedModesNavButton1",
                    className: "active",
                },
                markers: false,
            },
        })
            .fromTo("#fullyImmersiveScene", { y: "0%", x: "0%", scale: 0.6 }, { y: "-30%", x: "-20%", scale: 0.75, ease: "linear", duration: 0.5 })
            .fromTo("#hyperPersonalizedModes .navButtons", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #hearContent", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "-=0.5")
            .fromTo("#hyperPersonalizedModes #hearContent", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "+=1");
        this._scrollTimelines.push(hearTimeline);

        const feelTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'feelTimeline',
                trigger: '#feelTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                toggleClass: {
                    targets: "#hyperPersonalizedModesNavButton2",
                    className: "active",
                },
                markers: false,
            },
        })
            .to({ frame: 354 }, {
                frame: 365,
                duration: 0.3,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#hyperPersonalizedModes #feelContent", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #feelContent", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "+=1");
        this._scrollTimelines.push(feelTimeline);

        const immerseTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'immerseTimeline',
                trigger: '#immerseTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                toggleClass: {
                    targets: "#hyperPersonalizedModesNavButton3",
                    className: "active",
                },
                markers: false,
            },
        })
            .to({ frame: 380 }, {
                frame: 390,
                duration: 0.3,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#hyperPersonalizedModes #immerseContent", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #immerseContent", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "+=1");
        this._scrollTimelines.push(immerseTimeline);

        const beastTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'beastTimeline',
                trigger: '#beastTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                toggleClass: {
                    targets: "#hyperPersonalizedModesNavButton4",
                    className: "active",
                },
                markers: false,
            },
        })
            .to({ frame: 405 }, {
                frame: 415,
                duration: 0.3,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#hyperPersonalizedModes #beastContent", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #beastContent", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "+=1")
            .fromTo("#hyperPersonalizedModes .navButtons", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "<")
            .fromTo("#fullyImmersiveScene", { autoAlpha: 1, y: "-30%" }, { autoAlpha: 0, y: "-50%", duration: 0.5 }, "<")
            // .fromTo("#uspNavButtons", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "<");
        this._scrollTimelines.push(beastTimeline);

        // const earPrintTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'earprintSection',
        //         trigger: '#earprintSection',
        //         start: 'top top',
        //         end: 'bottom bottom',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo("#earprintImage", { autoAlpha: 0 }, { autoAlpha: 1, ease: "linear", duration: 0.3 })
        //     .from("#earprintImage .curve", { duration: 0.5, drawSVG: "100% live" })
        //     .fromTo("#earprintPhone", { autoAlpha: 0, scale: 10 }, { autoAlpha: 1, scale: 1, ease: "linear", duration: 0.5 })
        //     .fromTo("#earprintImage", { scale: 1.4 }, { scale: 0.8, ease: "linear", duration: 0.5 }, "<")
        //     .fromTo("#earprintImages .wrapper .wrapperInner", { x: "0%" }, { x: "-42%", ease: "linear", duration: 0.5 }, "-=0.1")
        //     .fromTo("#earprintTexts .wrapper", { autoAlpha: 0, x: 30, y: 0 }, { autoAlpha: 1, x: 0, y: 0, duration: 0.5 }, "-=0.5")
        //     .fromTo("#earprintImages, #earprintTexts .wrapper", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 }, "+=1");
        // this._scrollTimelines.push(earPrintTimeline);

        const aboveAndBeyondTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'aboveAndBeyondSection',
                trigger: '#aboveAndBeyondSection',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                markers: false,
            },
        })
            .fromTo("#aboveAndBeyondContent .title", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 })
            .fromTo("#aboveAndBeyondContent .title", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -30, duration: 0.5 }, "+=1");
        this._scrollTimelines.push(aboveAndBeyondTimeline);

        const otherFeaturesTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'otherFeaturesSection',
                trigger: '#otherFeaturesSection',
                start: 'top top',
                end: 'bottom bottom',
                scrub: true,
                markers: false,
                onUpdate: (self) => {
                    // Control carousel based on scroll progress
                    if (this._otherFeaturesCarousel) {
                        const progress = self.progress;
                        const totalSlides = this._otherFeaturesCarousel.slides.length;
                        // Map scroll progress (0-1) to slide index (0 to totalSlides-1)
                        // Handle edge case: progress = 1.0 should map to last slide
                        const targetSlideIndex = progress >= 1 ? totalSlides - 1 : Math.min(Math.floor(progress * totalSlides), totalSlides - 1);
                        const currentSlideIndex = this._otherFeaturesCarousel.realIndex;
                        
                        // Clear any pending autoplay resume timeout
                        if (this._otherFeaturesAutoplayResumeTimeout) {
                            clearTimeout(this._otherFeaturesAutoplayResumeTimeout);
                            this._otherFeaturesAutoplayResumeTimeout = null;
                        }
                        
                        // Only update if we need to change slides
                        if (targetSlideIndex !== currentSlideIndex && targetSlideIndex >= 0 && targetSlideIndex < totalSlides) {
                            // Temporarily stop autoplay while scrolling
                            const wasAutoplayRunning = this._otherFeaturesCarousel.autoplay.running;
                            if (wasAutoplayRunning) {
                                this._otherFeaturesCarousel.autoplay.pause();
                            }
                            
                            this._otherFeaturesCarousel.slideToLoop(targetSlideIndex);
                        }
                    }
                    
                    // When section becomes sticky (at start of scroll), make it fully visible
                    if (self.progress <= 0.01 || self.direction === 1) {
                        gsap.to("#otherFeaturesSection", {
                            autoAlpha: 1,
                            duration: 0.3,
                            ease: "power2.out"
                        });
                    }
                },
                onLeave: () => {
                    if (this._otherFeaturesCarousel && this._otherFeaturesCarousel.autoplay.paused) {
                        this._otherFeaturesCarousel.autoplay.resume();
                    }
                },
                onLeaveBack: () => {
                    // When scrolling up and leaving the section, reveal the above & beyond copy again
                    gsap.to("#aboveAndBeyondSection", {
                        autoAlpha: 1,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                    // Fade out otherFeaturesSection when scrolling up
                    gsap.to("#otherFeaturesSection", {
                        autoAlpha: 0,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                },
                onEnterBack: () => {
                    // When entering back, pause autoplay to let scroll control take over
                    if (this._otherFeaturesCarousel && this._otherFeaturesCarousel.autoplay.running) {
                        this._otherFeaturesCarousel.autoplay.pause();
                    }
                    // Hide aboveAndBeyondSection while feature cards are active
                    gsap.to("#aboveAndBeyondSection", {
                        autoAlpha: 0,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                    // Make otherFeaturesSection fully visible when it becomes sticky
                    gsap.to("#otherFeaturesSection", {
                        autoAlpha: 1,
                        duration: 0.3,
                        ease: "power2.out"
                    });
                },
                onEnter: () => {
                    // When entering the section (scrolling down), hide the above & beyond copy and show cards
                    gsap.to("#aboveAndBeyondSection", {
                        autoAlpha: 0,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                    gsap.to("#otherFeaturesSection", {
                        autoAlpha: 1,
                        duration: 0.3,
                        ease: "power2.out"
                    });
                },
            },
        })
            .fromTo("#otherFeaturesSection .imageArea", { autoAlpha: 1, x: 30 }, { autoAlpha: 1, x: 0, duration: 0.5 });
        
        // Only add carousel animations if the carousel exists
        if (this._otherFeaturesCarousel) {
            otherFeaturesTimeline
                .fromTo("#otherFeaturesCarousel", { autoAlpha: 1, x: -30 }, { autoAlpha: 1, x: 0, duration: 0.5 }, "<")
                .fromTo("#otherFeaturesContent .toolbar", { autoAlpha: 1 }, { autoAlpha: 1, duration: 0.5 }, "<")
                .to("#otherFeaturesCarousel", {
                    duration: 0.0001,
                    onStart: () => {
                        this._otherFeaturesCarousel.slideTo(0);
                        this._otherFeaturesCarousel.autoplay.start();
                    },
                    onReverseComplete: () => {
                        this._otherFeaturesCarousel.autoplay.stop();
                        this._otherFeaturesCarousel.slideTo(0);
                    },
                });
        }
        
        this._scrollTimelines.push(otherFeaturesTimeline);
    }

    _setupScrollTimelinesForLargeViewport() {
        const self = this;

        const heroTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'hero',
                trigger: 'body',
                start: 'top top',
                end: 'bottom bottom',
                endTrigger: '#heroSection',
                scrub: 0,
                markers: false,
            },
        })
            .fromTo('#heroHeadphone', { scale: 1 }, { scale: 2.1, ease: 'linear', duration: 0.5 })
            .fromTo('#heroTexts', { scale: 1 }, { scale: 0.9, duration: 0.5 }, '<')
            .fromTo('#heroText2Line1', { backgroundPosition: '0%' }, { backgroundPosition: '-60%', ease: 'linear', duration: 0.5 }, '<')
            .fromTo('#heroHeadphone', { scale: 2.1 }, { scale: 3.24, ease: 'linear', duration: 0.3 })
            .fromTo('#heroText2Line1', { backgroundPosition: '-60%' }, { backgroundPosition: '0%', ease: 'linear', duration: 0.3 }, '<')
            .fromTo('#heroHeadphone', { scale: 3.24 }, { scale: 8, ease: 'linear', duration: 0.5 })
            .fromTo('#heroText2Line1', { backgroundPosition: '0%' }, { backgroundPosition: '-100%', duration: 0.5 }, '<')
            .fromTo('#heroText1Wrapper', { autoAlpha: 1, scale: 1, y: 0 }, { autoAlpha: 0, scale: 0.9, y: -80, duration: 0.5 }, '<')
            .fromTo('#heroText2Wrapper', { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -100, duration: 0.5 }, '<')
            .fromTo('#heroText3Wrapper', { autoAlpha: 1, scale: 1, y: 0 }, { autoAlpha: 0, scale: 0.9, y: -10, duration: 0.5 }, '<')
            .fromTo('#heroHeadphone', { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.000000001 });
        this._scrollTimelines.push(heroTimeline);

        // const reviewersTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'reviewersSection',
        //         trigger: '#reviewersSection',
        //         start: 'top 85%',
        //         end: '+=200',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo('#reviewersSection .title span', { autoAlpha: 0, y: 50 }, { autoAlpha: 1, y: 0, stagger: { each: 0.1 }, duration: 0.5 })
        //     .fromTo('#reviewersSection .background', { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5 }, "-=0.4")
        //     .fromTo('#reviewersCarousel', { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<");
        // this._scrollTimelines.push(reviewersTimeline);

        // const reviewTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'reviewSection',
        //         trigger: '#reviewSection',
        //         start: 'top 75%',
        //         end: '+=200',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo('#reviewText div > span', { autoAlpha: 0, y: 5 }, { autoAlpha: 1, y: 0, stagger: { each: 0.025 }, duration: 0.5 });
        // this._scrollTimelines.push(reviewTimeline);

        // const newBreedHeadphoneTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'newBreedHeadphonesSection',
        //         trigger: '#newBreedHeadphonesSection',
        //         start: 'top 70%',
        //         end: 'bottom bottom',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo('#newBreedHeadphonesScene', { y: "5%" }, { y: "0%", duration: 0.81 })
        //     .to({ frame: 149 }, {
        //         frame: 0,
        //         duration: 0.5,
        //         onUpdate: function () {
        //             const frame = this.targets()[0].frame;
        //             self._newBreedHeadphoneAnimation.goToAndStop(frame, true);
        //         }
        //     })
        //     .to({ frame: 0 }, {
        //         frame: 149,
        //         duration: 0.5,
        //         onUpdate: function () {
        //             const frame = this.targets()[0].frame;
        //             self._newBreedHeadphoneAnimation.goToAndStop(frame, true);
        //         }
        //     });
        // this._scrollTimelines.push(newBreedHeadphoneTimeline);

        // const newBreedTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'newBreedSection',
        //         trigger: '#newBreedSection',
        //         start: 'top bottom',
        //         end: 'bottom bottom',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo('#orangeBackground', { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3 })
        //     .fromTo('#newBreedTexts', { y: 80 }, { y: 80, duration: 0.5 }, "<")
        //     .fromTo('#newBreedTitleLine1', { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
        //     .fromTo('#newBreedTitleLine1 .gradientText', { backgroundPosition: '100%' }, { backgroundPosition: '0%', duration: 0.5 }, "<")
        //     .fromTo('#newBreedTitleLine2', { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "-=0.5")
        //     .fromTo('#orangeBackground', { backgroundSize: "94.6vw 94.6vw" }, { backgroundSize: "120vw 120vw", ease: "power1.easeInOut", duration: 0.5 })
        //     .fromTo('#newBreedTexts', { y: 80 }, { y: 0, duration: 0.5 }, "<")
        //     .fromTo('#newBreedText1 div > span', { autoAlpha: 0, y: 5 }, { autoAlpha: 1, y: 0, stagger: { each: 0.025 }, duration: 0.5 }, "<")
        //     .fromTo('#newBreedTitleLine1 .gradientText', { backgroundPosition: '0%' }, { backgroundPosition: '100%', duration: 0.5 }, "<")
        //     .fromTo('#orangeBackground', { backgroundSize: "120vw 120vw" }, { backgroundSize: "200vw 200vw", ease: "power1.easeInOut", duration: 0.5 }, "+=0.5")
        //     .fromTo('#newBreedTitleLine1 .gradientText', { backgroundPosition: '100%' }, { backgroundPosition: '0%', duration: 0.5 }, "<")
        //     .fromTo('#newBreedTitleLine1', { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -100, duration: 0.5, ease: "linear" }, "+=0.5")
        //     .fromTo('#newBreedTitleLine2', { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -70, duration: 0.5, ease: "linear" }, "<")
        //     .fromTo('#newBreedTextWrapper', { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -50, duration: 0.5, ease: "linear" }, "<")
        //     .fromTo('#orangeBackground', { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.6 }, "<");
        // this._scrollTimelines.push(newBreedTimeline);

        // const eargasmTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'eargasmSection',
        //         trigger: '#eargasmSection',
        //         start: 'top top',
        //         end: 'bottom bottom',
        //         scrub: 0,
        //         onEnter: () => {
        //             document.querySelector("#uspNavButton1").classList.add("active");
        //         },
        //         onLeaveBack: () => {
        //             document.querySelector("#uspNavButton1").classList.remove("active");
        //         },
        //         markers: false,
        //     },
        // })
        //     .fromTo("#eargasmScene", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.2 })
        //     .fromTo("#eargasmTexts", { autoAlpha: 0, scale: 0.95 }, { autoAlpha: 1, scale: 1, duration: 0.3 }, "<")
        //     .to({ frame: 0 }, {
        //         frame: 30,
        //         ease: "linear",
        //         duration: 0.2,
        //         onUpdate: function () {
        //             const frame = this.targets()[0].frame;
        //             self._eargasmAnimation.goToAndStop(frame, true);
        //         }
        //     }, "-=0.5")
        //     .fromTo("#eargasmTexts", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.2 })
        //     .to({ frame: 30 }, {
        //         frame: 111,
        //         ease: "linear",
        //         duration: 0.2,
        //         onUpdate: function () {
        //             const frame = this.targets()[0].frame;
        //             self._eargasmAnimation.goToAndStop(frame, true);
        //         }
        //     }, "<")
        //     .fromTo("#eargasmScene", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.1 });
        // this._scrollTimelines.push(eargasmTimeline);

        /* ====================================================================
         * EXPLODED HEADPHONE — DESKTOP (large viewport) scroll timeline
         *
         * Same phase structure as mobile (see the small-viewport timeline for
         * the full phase-by-phase description), with two differences:
         *   - Both driver labels are shown TOGETHER, which keeps the total
         *     scroll shorter — retained by design.
         * NOTE: an earlier pass zoomed the resting pose to scale 1.5 to cut
         * whitespace, but that pushed parts of the headphone outside the
         * #explodedContent overflow:hidden stage and clipped it. Reverted
         * to the original, non-clipping y:-22vh / scale:1.3 resting pose.
         * ==================================================================*/
        const explodedTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'explodedSection',
                trigger: '#explodedSection',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                markers: false,
            },
        })
            // Phase 1 — spacer only. The scene's fade-in was moved out of the
            // pinned range: it now crossfades with the FEEL Music title during
            // the 100vh BEFORE the pin (see _addFeelMusicTitleFadeOutTimeline),
            // so the stage is never blank. This empty tween must NOT touch
            // autoAlpha (two writers would fight/flash); it only preserves the
            // original scroll-length proportions of the later phases.
            .to({}, { duration: 0.3 })
            // Phase 2 — intro collapse: fully exploded (114) -> assembled (0).
            .to({ frame: 114 }, {
                frame: 0,
                ease: "power1.easeOut",
                duration: 1.1,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._explodedAnimation, frame, [EXPLODED_FIRST_FRAME, EXPLODED_LAST_FRAME]);
                }
            })
            // Wrapper glides from the large intro pose into the resting
            // pose ("<" = starts together with the collapse).
            .fromTo("#explodedSceneWrapper", { x: "-3vh", y: "-13vh", scale: 1.35 }, { x: "0vh", y: "-22vh", scale: 1.3, duration: 1.1 }, "<")
            // Intro texts (kept for compatibility; section markup currently
            // has #explodedTexts commented out).
            .fromTo("#explodedTexts", { autoAlpha: 0, scale: 0.9 }, { autoAlpha: 1, scale: 1, duration: 0.3 }, "-=0.5")
            .fromTo("#explodedTexts", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 }, "+=0.25")
            // Phase 3 — minor explosion: assembled (0) -> mid pose (82).
            // Longer duration = more scroll distance = readable, gradual explode.
            .to({ frame: 0 }, {
                frame: EXPLODED_MIDDLE_FRAME,
                ease: "power1.easeIn",
                duration: 0.5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._explodedAnimation, frame, [EXPLODED_FIRST_FRAME, EXPLODED_MIDDLE_FRAME]);
                }
            })
            // Phase 4 — BOTH driver labels appear together ("<" = same time),
            // are held on screen, then fade out together.
            .fromTo("#explodedLabel1", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3 })
            .fromTo("#explodedLabel2", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3 }, "<")
            .fromTo("#explodedLabel1", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 }, "+=0.6")
            .fromTo("#explodedLabel2", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 }, "<")
            // Phase 5 — full explosion: mid pose (82) -> fully exploded (114).
            .to({ frame: EXPLODED_MIDDLE_FRAME }, {
                frame: 114,
                ease: "power1.easeInOut",
                duration: 0.5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._explodedAnimation, frame, [EXPLODED_MIDDLE_FRAME, EXPLODED_LAST_FRAME]);
                }
            }, "+=0.35")
            // Phase 6 — fade the scene out.
            .fromTo("#explodedSceneWrapper", { autoAlpha: 1, scale: 1.3 }, { autoAlpha: 0, scale: 0.9, duration: 0.3 });
        this._scrollTimelines.push(explodedTimeline);

        const fullyImmersiveTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'fullyImmersiveSection',
                trigger: '#fullyImmersiveSection',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                onEnter: () => {
                    // document.querySelector("#uspNavButton1").classList.remove("active");
                    document.querySelector("#uspNavButton2").classList.add("active");
                },
                onLeaveBack: () => {
                    // document.querySelector("#uspNavButton1").classList.add("active");
                    document.querySelector("#uspNavButton2").classList.remove("active");
                },
                markers: false,
            },
        })
            .fromTo("#fullyImmersiveScene", { y: "100%" }, { y: "0%", ease: "linear", duration: 0.5 })
            .fromTo("#fullyImmersiveScene", { scale: 0.9 }, { scale: 1, ease: "linear", duration: 0.5 })
            .to({ frame: 1 }, {
                frame: 29,
                duration: 1,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#fullyImmersiveText1", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 })
            .to({ frame: 29 }, {
                frame: 89,
                duration: 2,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            }, "<")
            .to("#fullyImmersiveScene", {
                onStart: function () {
                    document.querySelector("#fullyImmersiveScene").classList.add("topAligned");
                },
                onReverseComplete: function () {
                    document.querySelector("#fullyImmersiveScene").classList.remove("topAligned");
                },
            })
            .fromTo("#fullyImmersiveText1", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -30, duration: 0.3 }, "-=0.5")
            .fromTo("#fullyImmersiveText2", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.3 })
            .to({ frame: 89 }, {
                frame: 110,
                duration: 0.6,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#fullyImmersiveText2", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 })
            .to({ frame: 110 }, {
                frame: 321,
                duration: 4.5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            }, "<")
            .fromTo("#fullyImmersiveScene", { scale: 1 }, { scale: 0.55, ease: "linear", duration: 0.3 })
            .to({ frame: 321 }, {
                // Clamped from 339: source frames ~332-337 of the JPG sequence
                // crop the mode dial at the bottom of the render, so the scrub
                // stops before them (scene is fading out here anyway).
                frame: 330,
                duration: 0.3,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            }, "<")
            .fromTo("#fullyImmersiveText3 p", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.3 })
            .fromTo("#fullyImmersiveText3 h3", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.3 }, "-=0.25")
            .fromTo("#fullyImmersiveText3 blockquote", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.3 })
            .fromTo("#fullyImmersiveText3", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -30, duration: 0.3 }, "+=0.5");
        this._scrollTimelines.push(fullyImmersiveTimeline);

        const hyperPersonalizedIntroTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'hyperPersonalizedIntroTimeline',
                trigger: '#hyperPersonalizedIntroTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                onEnter: () => {
                    document.querySelector("#uspNavButton2").classList.remove("active");
                    document.querySelector("#uspNavButton3").classList.add("active");
                },
                onLeaveBack: () => {
                    document.querySelector("#uspNavButton2").classList.add("active");
                    document.querySelector("#uspNavButton3").classList.remove("active");
                },
                markers: false,
            },
        })
            .to({ frame: 339 }, {
                frame: 354,
                duration: 0.5,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#fullyImmersiveScene", { y: 0, scale: 0.55 }, { y: "-35%", scale: 1, ease: "linear", duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedIntroContent h3", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedIntroContent p", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "-=0.5")
            .fromTo("#hyperPersonalizedIntroContent", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -30, duration: 0.5 }, "+=0.5");
        this._scrollTimelines.push(hyperPersonalizedIntroTimeline);

        const hearTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'hearTimeline',
                trigger: '#hearTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                toggleClass: {
                    targets: "#hyperPersonalizedModesNavButton1",
                    className: "active",
                },
                markers: false,
            },
        })
            .fromTo("#fullyImmersiveScene", { y: "-35%", x: "0%" }, { y: "-55%", x: "-20%", ease: "linear", duration: 0.5 })
            .fromTo("#hyperPersonalizedModes .navButtons", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #hearContent", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "-=0.5")
            .fromTo("#hyperPersonalizedModes #hearContent", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: 30, duration: 0.5 }, "+=1");
        this._scrollTimelines.push(hearTimeline);

        const feelTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'feelTimeline',
                trigger: '#feelTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                toggleClass: {
                    targets: "#hyperPersonalizedModesNavButton2",
                    className: "active",
                },
                markers: false,
            },
        })
            .to({ frame: 354 }, {
                frame: 365,
                duration: 0.3,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#hyperPersonalizedModes #hearContent", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: 30, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #feelContent", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #feelContent", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: 30, duration: 0.5 }, "+=1");
        this._scrollTimelines.push(feelTimeline);

        const immerseTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'immerseTimeline',
                trigger: '#immerseTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                toggleClass: {
                    targets: "#hyperPersonalizedModesNavButton3",
                    className: "active",
                },
                markers: false,
            },
        })
            .to({ frame: 380 }, {
                frame: 390,
                duration: 0.3,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#hyperPersonalizedModes #feelContent", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: 30, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #immerseContent", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #immerseContent", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: 30, duration: 0.5 }, "+=1");
        this._scrollTimelines.push(immerseTimeline);

        const beastTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'beastTimeline',
                trigger: '#beastTimeline',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                toggleClass: {
                    targets: "#hyperPersonalizedModesNavButton4",
                    className: "active",
                },
                markers: false,
            },
        })
            .to({ frame: 405 }, {
                frame: 415,
                duration: 0.3,
                onUpdate: function () {
                    const frame = this.targets()[0].frame;
                    self._goToLottieFrame(self._fullyImmersiveAnimation, frame);
                }
            })
            .fromTo("#hyperPersonalizedModes #immerseContent", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: 30, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #beastContent", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 }, "<")
            .fromTo("#hyperPersonalizedModes #beastContent", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: 30, duration: 0.5 }, "+=1")
            .fromTo("#hyperPersonalizedModes .navButtons", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "<")
            .fromTo("#fullyImmersiveScene", { autoAlpha: 1, y: "-55%" }, { autoAlpha: 0, y: "-65%", duration: 0.5 }, "<")
            // .fromTo("#uspNavButtons", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, "<");
        this._scrollTimelines.push(beastTimeline);

        // const earPrintTimeline = gsap.timeline({
        //     scrollTrigger: {
        //         id: 'earprintSection',
        //         trigger: '#earprintSection',
        //         start: 'top top',
        //         end: 'bottom bottom',
        //         scrub: 0,
        //         markers: false,
        //     },
        // })
        //     .fromTo("#earprintImage", { autoAlpha: 0 }, { autoAlpha: 1, ease: "linear", duration: 0.3 })
        //     .from("#earprintImage .curve", { duration: 0.5, drawSVG: "100% live" })
        //     .fromTo("#earprintPhone", { autoAlpha: 0, scale: 10 }, { autoAlpha: 1, scale: 1, ease: "linear", duration: 0.5 })
        //     .fromTo("#earprintImage", { scale: 1.4 }, { scale: 0.8, ease: "linear", duration: 0.5 }, "<")
        //     .fromTo("#earprintImages .wrapper .wrapperInner", { x: "0%" }, { x: "-42%", ease: "linear", duration: 0.5 }, "-=0.1")
        //     .fromTo("#earprintTexts .wrapper", { autoAlpha: 0, x: 30, y: 0 }, { autoAlpha: 1, x: 0, y: 0, duration: 0.5 }, "-=0.5")
        //     .fromTo("#earprintImages, #earprintTexts .wrapper", { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.3 }, "+=1");
        // this._scrollTimelines.push(earPrintTimeline);

        const aboveAndBeyondTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'aboveAndBeyondSection',
                trigger: '#aboveAndBeyondSection',
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0,
                markers: false,
            },
        })
            .fromTo("#aboveAndBeyondContent .title", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.5 })
            .fromTo("#aboveAndBeyondContent .title", { autoAlpha: 1, y: 0 }, { autoAlpha: 0, y: -30, duration: 0.5 }, "+=1");
        this._scrollTimelines.push(aboveAndBeyondTimeline);

        const otherFeaturesTimeline = gsap.timeline({
            scrollTrigger: {
                id: 'otherFeaturesSection',
                trigger: '#otherFeaturesSection',
                start: 'top top',
                end: 'bottom bottom',
                scrub: true,
                markers: false,
                onUpdate: (self) => {
                    // Control carousel based on scroll progress
                    if (this._otherFeaturesCarousel) {
                        const progress = self.progress;
                        const totalSlides = this._otherFeaturesCarousel.slides.length;
                        // Map scroll progress (0-1) to slide index (0 to totalSlides-1)
                        // Handle edge case: progress = 1.0 should map to last slide
                        const targetSlideIndex = progress >= 1 ? totalSlides - 1 : Math.min(Math.floor(progress * totalSlides), totalSlides - 1);
                        const currentSlideIndex = this._otherFeaturesCarousel.realIndex;
                        
                        // Clear any pending autoplay resume timeout
                        if (this._otherFeaturesAutoplayResumeTimeout) {
                            clearTimeout(this._otherFeaturesAutoplayResumeTimeout);
                            this._otherFeaturesAutoplayResumeTimeout = null;
                        }
                        
                        // Only update if we need to change slides
                        if (targetSlideIndex !== currentSlideIndex && targetSlideIndex >= 0 && targetSlideIndex < totalSlides) {
                            // Temporarily stop autoplay while scrolling
                            const wasAutoplayRunning = this._otherFeaturesCarousel.autoplay.running;
                            if (wasAutoplayRunning) {
                                this._otherFeaturesCarousel.autoplay.pause();
                            }
                            
                            this._otherFeaturesCarousel.slideToLoop(targetSlideIndex);
                        }
                    }
                    
                    // When section becomes sticky (at start of scroll), make it fully visible
                    if (self.progress <= 0.01 || self.direction === 1) {
                        gsap.to("#otherFeaturesSection", {
                            autoAlpha: 1,
                            duration: 0.3,
                            ease: "power2.out"
                        });
                    }
                },
                onLeave: () => {
                    if (this._otherFeaturesCarousel && this._otherFeaturesCarousel.autoplay.paused) {
                        this._otherFeaturesCarousel.autoplay.resume();
                    }
                },
                onLeaveBack: () => {
                    // When scrolling up and leaving the section, reveal the above & beyond copy again
                    gsap.to("#aboveAndBeyondSection", {
                        autoAlpha: 1,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                    // Fade out otherFeaturesSection when scrolling up
                    gsap.to("#otherFeaturesSection", {
                        autoAlpha: 0,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                },
                onEnterBack: () => {
                    // When entering back, pause autoplay to let scroll control take over
                    if (this._otherFeaturesCarousel && this._otherFeaturesCarousel.autoplay.running) {
                        this._otherFeaturesCarousel.autoplay.pause();
                    }
                    // Hide aboveAndBeyondSection while feature cards are active
                    gsap.to("#aboveAndBeyondSection", {
                        autoAlpha: 0,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                    // Make otherFeaturesSection fully visible when it becomes sticky
                    gsap.to("#otherFeaturesSection", {
                        autoAlpha: 1,
                        duration: 0.3,
                        ease: "power2.out"
                    });
                },
                onEnter: () => {
                    // When entering the section (scrolling down), hide the above & beyond copy and show cards
                    gsap.to("#aboveAndBeyondSection", {
                        autoAlpha: 0,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                    gsap.to("#otherFeaturesSection", {
                        autoAlpha: 1,
                        duration: 0.3,
                        ease: "power2.out"
                    });
                },
            },
        })
            .fromTo("#otherFeaturesSection .imageArea", { autoAlpha: 1, x: 30 }, { autoAlpha: 1, x: 0, duration: 0.5 });
        
        // Only add carousel animations if the carousel exists
        if (this._otherFeaturesCarousel) {
            otherFeaturesTimeline
                .fromTo("#otherFeaturesCarousel", { autoAlpha: 1, x: -30 }, { autoAlpha: 1, x: 0, duration: 0.5 }, "<")
                .fromTo("#otherFeaturesContent .toolbar", { autoAlpha: 1 }, { autoAlpha: 1, duration: 0.5 }, "<")
                .to("#otherFeaturesCarousel", {
                    duration: 0.0001,
                    onStart: () => {
                        this._otherFeaturesCarousel.slideTo(0);
                        this._otherFeaturesCarousel.autoplay.start();
                    },
                    onReverseComplete: () => {
                        this._otherFeaturesCarousel.autoplay.stop();
                        this._otherFeaturesCarousel.slideTo(0);
                    },
                });
        }
        
        this._scrollTimelines.push(otherFeaturesTimeline);
    }

    _prePlayTimelines() {
        // Preplay animations to ensure smoothness on animatiing during
        // window scroll. Note that we play animations in reverse order.
        for (let index = this._scrollTimelines.length - 1; index >= 0; index--) {
            const timeline = this._scrollTimelines[index];
            timeline.progress(1).progress(0);
        }
    }
}



// Feature Cards Animation - Sticky Scroll Effect
"use strict";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

class FeatureCardsAnimation {
    constructor() {
        this.cards = document.querySelectorAll('.feature-card');
        this.section = document.querySelector('#otherFeaturesSection');
        this.currentCardIndex = 0;
        this.isInSection = false;
        this.lastUpdateTime = 0;
        this.updateDelay = 100; // Debounce delay in milliseconds
        this.init();
    }

    init() {
        if (this.cards.length === 0 || !this.section) {
            console.log('FeatureCardsAnimation: No cards or section found');
            return;
        }

        console.log('FeatureCardsAnimation: Found', this.cards.length, 'cards');
        this.setupScrollTrigger();
        this.hideAllCards();
        
        // Show first card immediately
        this.showCard(0);
        
        // Refresh scroll trigger on window resize
        window.addEventListener('resize', () => {
            ScrollTrigger.refresh();
        });
        
        // Add backup scroll listener in case ScrollTrigger fails
        this.addBackupScrollListener();
    }

    setupScrollTrigger() {
        // Get header height dynamically for better responsiveness
        const headerHeight = window.innerWidth <= 480 ? 80 : 120;
        
        console.log('Setting up scroll trigger for', this.cards.length, 'cards');
        
        // Create scroll trigger for the section with unique ID
        ScrollTrigger.create({
            id: 'featureCardsSticky',
            trigger: this.section,
            start: `top ${headerHeight}px`, // When section reaches header position
            end: `bottom ${headerHeight}px`,
            scrub: 1, // Add scrub for smoother animation
            onEnter: () => {
                console.log('Entered section');
                this.isInSection = true;
                this.showCard(0);
            },
            onLeave: () => {
                console.log('Left section');
                this.isInSection = false;
                // Don't hide all cards when leaving, keep the last one visible
                // this.hideAllCards();
            },
            onEnterBack: () => {
                console.log('Entered back from bottom');
                this.isInSection = true;
                // Show the last card when entering back from bottom
                this.showCard(this.cards.length - 1);
            },
            onLeaveBack: () => {
                console.log('Left back to top');
                this.isInSection = false;
                this.hideAllCards();
            },
            onUpdate: (self) => {
                if (!this.isInSection) return;
                
                // Calculate which card should be visible based on scroll progress
                const progress = self.progress;
                const totalCards = this.cards.length;
                
                // More precise card calculation
                let cardIndex;
                if (progress < 0.25) {
                    cardIndex = 0;
                } else if (progress < 0.5) {
                    cardIndex = 1;
                } else if (progress < 0.75) {
                    cardIndex = 2;
                } else {
                    cardIndex = 3;
                }
                
                // Ensure we don't go beyond the last card
                const targetCardIndex = Math.min(cardIndex, totalCards - 1);
                
                console.log('Scroll progress:', progress, 'Card index:', cardIndex, 'Target:', targetCardIndex, 'Current:', this.currentCardIndex);
                
                if (targetCardIndex !== this.currentCardIndex) {
                    console.log('Switching from card', this.currentCardIndex, 'to card', targetCardIndex);
                    this.hideCard(this.currentCardIndex);
                    this.currentCardIndex = targetCardIndex;
                    this.showCard(this.currentCardIndex);
                }
            }
        });
    }

    showCard(index) {
        if (index >= 0 && index < this.cards.length) {
            console.log('Showing card', index);
            
            // Hide all cards first with explicit styling
            this.cards.forEach((card, i) => {
                card.classList.remove('visible');
                card.style.opacity = '0';
                card.style.transform = 'translate(-50%, -50%) scale(0.95)';
                card.style.zIndex = '1';
            });
            
            // Show the specific card with explicit styling
            const card = this.cards[index];
            card.classList.add('visible');
            card.style.opacity = '1';
            card.style.transform = 'translate(-50%, -50%) scale(1)';
            card.style.zIndex = '10';
            
            console.log('Card', index, 'classes:', card.className, 'opacity:', card.style.opacity);
        }
    }

    hideCard(index) {
        if (index >= 0 && index < this.cards.length) {
            const card = this.cards[index];
            card.classList.remove('visible');
        }
    }

    hideAllCards() {
        this.cards.forEach((card, index) => {
            card.classList.remove('visible');
            card.style.opacity = '0';
            card.style.transform = 'translate(-50%, -50%) scale(0.95)';
            card.style.zIndex = '1';
        });
        this.currentCardIndex = 0;
    }
    
    addBackupScrollListener() {
        let isScrolling = false;
        
        window.addEventListener('scroll', () => {
            if (isScrolling) return;
            
            isScrolling = true;
            requestAnimationFrame(() => {
                const sectionRect = this.section.getBoundingClientRect();
                const headerHeight = window.innerWidth <= 480 ? 80 : 120;
                
                // Check if section is in view
                if (sectionRect.top <= headerHeight && sectionRect.bottom >= headerHeight) {
                    this.isInSection = true;
                    
                    // Calculate scroll progress within the section
                    const sectionHeight = sectionRect.height;
                    const scrolledPast = headerHeight - sectionRect.top;
                    const progress = Math.min(Math.max(scrolledPast / sectionHeight, 0), 1);
                    
                    // Calculate which card should be visible
                    let cardIndex;
                    if (progress < 0.25) {
                        cardIndex = 0;
                    } else if (progress < 0.5) {
                        cardIndex = 1;
                    } else if (progress < 0.75) {
                        cardIndex = 2;
                    } else {
                        cardIndex = 3;
                    }
                    
                    const targetCardIndex = Math.min(cardIndex, this.cards.length - 1);
                    
                    console.log('Backup scroll - Progress:', progress, 'Card:', targetCardIndex, 'Current:', this.currentCardIndex);
                    
                    if (targetCardIndex !== this.currentCardIndex) {
                        console.log('Backup switching to card', targetCardIndex);
                        this.hideCard(this.currentCardIndex);
                        this.currentCardIndex = targetCardIndex;
                        this.showCard(this.currentCardIndex);
                    }
                } else {
                    this.isInSection = false;
                }
                
                isScrolling = false;
            });
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    // Check if GSAP and ScrollTrigger are available
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        try {
            const featureCardsAnimation = new FeatureCardsAnimation();
            window.featureCardsAnimation = featureCardsAnimation;
            
            // Add hover effects
            document.querySelectorAll('.feature-card').forEach(card => {
                card.addEventListener('mouseenter', () => {
                    gsap.to(card.querySelector('.card-content'), { y: -5, duration: 0.3, ease: "power2.out" });
                });
                card.addEventListener('mouseleave', () => {
                    gsap.to(card.querySelector('.card-content'), { y: 0, duration: 0.3, ease: "power2.out" });
                });
            });
        } catch (error) {
            console.warn('Feature cards animation failed to initialize:', error);
            // Fallback: show all cards if animation fails
            document.querySelectorAll('.feature-card').forEach((card, index) => {
                if (index === 0) {
                    card.classList.add('visible');
                    card.style.opacity = '1';
                    card.style.transform = 'translate(-50%, -50%) scale(1)';
                }
            });
        }
    } else {
        console.warn('GSAP or ScrollTrigger not available, showing first card only');
        // Fallback: show first card if GSAP is not available
        const firstCard = document.querySelector('.feature-card');
        if (firstCard) {
            firstCard.classList.add('visible');
            firstCard.style.opacity = '1';
            firstCard.style.transform = 'translate(-50%, -50%) scale(1)';
        }
    }
});

// Test buttons removed - cards now work with scroll only

// 0.5 -> 0.3 , 1 -> 0.5

const homePage = new HomePage();
homePage.initialize();

// Note: Timeout interception moved to top of file for immediate execution

// Defer analytics network requests to improve page load performance
// Full-page loader waits for hero image + critical Lottie (max PAGE_LOADER_MAX_MS)
(function() {
    // Defer analytics after page is interactive
    const deferAnalytics = function() {
        // Defer Shopify Trekkie produce_batch requests
        if (window.Shopify && window.Shopify.analytics) {
            const originalPublish = window.Shopify.analytics.publish;
            if (originalPublish) {
                window.Shopify.analytics.publish = function() {
                    setTimeout(() => {
                        originalPublish.apply(this, arguments);
                    }, 2000);
                };
            }
        }

        // Defer Microsoft Clarity collect requests  
        if (window.clarity) {
            const originalClarity = window.clarity;
            window.clarity = function() {
                setTimeout(() => {
                    if (originalClarity) originalClarity.apply(this, arguments);
                }, 2000);
            };
        }
    };

    // Defer analytics after page load (non-blocking)
    if (document.readyState === 'complete') {
        setTimeout(deferAnalytics, 1000);
    } else {
        window.addEventListener('load', function() {
            setTimeout(deferAnalytics, 1000);
        });
    }
})();
